import React, { useState, useEffect } from 'react';
import { WORKER_API } from '../config.js';
import { fetchProtocolScore } from '../services/defiService';
import ApyTokenModal from './ApyTokenModal.jsx';

// Calculate grade from score (same logic as backend and homepage)
function calculateGrade(score) {
  if (score === null || score === undefined || typeof score !== 'number') {
    return 'N/A';
  }
  
  // Litepaper 标准：5 级评分系统
  if (score >= 90) return 'AAA';
  if (score >= 75) return 'AA-A';
  if (score >= 60) return 'BBB-B';
  if (score >= 40) return 'CCC';
  return 'D';
}

// Format timestamp to relative time in English (e.g., "2 hours ago", "3 days ago")
function formatTimeAgo(timestamp) {
  if (!timestamp) return "Unknown";
  
  try {
    const date = new Date(timestamp);
    if (isNaN(date.getTime())) return "Unknown";
    
    const now = new Date();
    const diffInSeconds = Math.floor((now - date) / 1000);
    
    if (diffInSeconds < 0) return "just now";
    if (diffInSeconds < 60) return "just now";
    
    const diffInMinutes = Math.floor(diffInSeconds / 60);
    if (diffInMinutes < 60) {
      return diffInMinutes === 1 ? "1 minute ago" : `${diffInMinutes} minutes ago`;
    }
    
    const diffInHours = Math.floor(diffInMinutes / 60);
    if (diffInHours < 24) {
      return diffInHours === 1 ? "1 hour ago" : `${diffInHours} hours ago`;
    }
    
    const diffInDays = Math.floor(diffInHours / 24);
    if (diffInDays < 30) {
      return diffInDays === 1 ? "1 day ago" : `${diffInDays} days ago`;
    }
    
    const diffInMonths = Math.floor(diffInDays / 30);
    if (diffInMonths < 12) {
      return diffInMonths === 1 ? "1 month ago" : `${diffInMonths} months ago`;
    }
    
    const diffInYears = Math.floor(diffInMonths / 12);
    return diffInYears === 1 ? "1 year ago" : `${diffInYears} years ago`;
  } catch {
    return 'Unknown';
  }
}

// Get score color class (same as homepage)
function getScoreColor(score) {
  if (score === null || score === undefined || typeof score !== 'number') {
    return 'bg-gray-100 text-gray-500';
  }
  const scoreValue = Math.round(score);
  if (scoreValue >= 90) return 'bg-green-100 text-green-800';
  if (scoreValue >= 75) return 'bg-yellow-100 text-yellow-800';
  return 'bg-rose-100 text-rose-800';
}

const getEmptyProtocolForm = () => ({
  name: '',
  blockchain: '',
  category: '',
  tvl: '',
  url: '',
  logo: '',
  description: '',
  slug: '',
  hidden: false
});

function Admin() {
  const [isAuthenticated, setIsAuthenticated] = useState(false);
  const [email, setEmail] = useState('');
  const [password, setPassword] = useState('');
  const [authToken, setAuthToken] = useState('');
  const [error, setError] = useState('');
  const [loading, setLoading] = useState(false);
  const [protocols, setProtocols] = useState([]);
  const [pagination, setPagination] = useState(null);
  const [currentPage, setCurrentPage] = useState(1);
  const [search, setSearch] = useState('');
  const [sortBy, setSortBy] = useState('tvl');
  
  // Filter states
  const [minTvl, setMinTvl] = useState('');
  const [maxTvl, setMaxTvl] = useState('');
  const [selectedCategories, setSelectedCategories] = useState([]);
  const [selectedBlockchains, setSelectedBlockchains] = useState([]);
  const [selectedScores, setSelectedScores] = useState([]);
  const [showFilters, setShowFilters] = useState(false);
  const [updating, setUpdating] = useState(false);
  const [updateStatus, setUpdateStatus] = useState('');
  const [updateLogs, setUpdateLogs] = useState([]);
  const [showConfirmDialog, setShowConfirmDialog] = useState(false);
  const [pendingUpdateList, setPendingUpdateList] = useState([]);
  const [selectedProtocolIds, setSelectedProtocolIds] = useState(new Set());
  const [updateMode, setUpdateMode] = useState('list'); // 'list' or 'selected'
  const [retryCount, setRetryCount] = useState(0);
  const [isRetrying, setIsRetrying] = useState(false);
  const [aiVersion, setAiVersion] = useState('2.0'); // AI 版本选择，默认 2.0
  const [activeTask, setActiveTask] = useState(null);
  const [isApyTokenModalOpen, setIsApyTokenModalOpen] = useState(false);
  const [isCreateModalOpen, setIsCreateModalOpen] = useState(false);
  const [newProtocolData, setNewProtocolData] = useState(() => getEmptyProtocolForm());
  const [creatingProtocol, setCreatingProtocol] = useState(false);
  const [createError, setCreateError] = useState('');
  const [visibilityUpdatingId, setVisibilityUpdatingId] = useState(null);
  const [faviconUpdatingId, setFaviconUpdatingId] = useState(null);
  const [autoFillLoading, setAutoFillLoading] = useState(false);

  const formatServerLogs = (logs) => {
    if (!Array.isArray(logs)) return [];
    return logs.map((log) => ({
      time: log.time ? new Date(log.time).toLocaleTimeString() : new Date().toLocaleTimeString(),
      message: log.message || ''
    }));
  };
  
  // Available options (will be populated from data)
  const [availableCategories, setAvailableCategories] = useState([]);
  const [availableBlockchains, setAvailableBlockchains] = useState([]);
  
  // Report modal states
  const [isReportModalOpen, setIsReportModalOpen] = useState(false);
  const [selectedProject, setSelectedProject] = useState(null);
  const [selectedReport, setSelectedReport] = useState(null);
  const [reportLoading, setReportLoading] = useState(false);
  const [reportError, setReportError] = useState(null);
  
  // History modal states
  const [isHistoryModalOpen, setIsHistoryModalOpen] = useState(false);
  const [historyProtocol, setHistoryProtocol] = useState(null);
  const [historyData, setHistoryData] = useState([]);
  const [historyLoading, setHistoryLoading] = useState(false);
  
  // Edit modal states
  const [isEditModalOpen, setIsEditModalOpen] = useState(false);
  const [editingProtocol, setEditingProtocol] = useState(null);
  const [editFormData, setEditFormData] = useState({
    name: '',
    category: '',
    blockchain: '',
    tvl: '',
    url: '',
    app_url: '',
    description: '',
    logo: '',
    slug: ''
  });
  const [editLoading, setEditLoading] = useState(false);
  const [logoSaving, setLogoSaving] = useState(false);
  
  // Config modal states
  const [isConfigModalOpen, setIsConfigModalOpen] = useState(false);
  const [configData, setConfigData] = useState({
    notificationEmail: '',
    updateInterval: '0 * * * *',
    twitterAutoPostEnabled: false,
    scoreAutoUpdateEnabled: false,
    scoreUpdateIntervalHigh: 7 * 24,    // 默认7天（168小时）
    scoreUpdateIntervalMedium: 14 * 24, // 默认14天（336小时）
    scoreUpdateIntervalLow: 30 * 24      // 默认30天（720小时）
  });
  const [twitterAutoPostStatus, setTwitterAutoPostStatus] = useState({
    enabled: false,
    todaySentCount: 0,
    todayLimit: 3,
    date: ''
  });
  const [configLoading, setConfigLoading] = useState(false);
  
  // Twitter modal states
  const [isTwitterModalOpen, setIsTwitterModalOpen] = useState(false);
  const [twitterTweetType, setTwitterTweetType] = useState('protocol_analysis'); // protocol_analysis, platform_update, industry_insight, news_alert, custom
  const [twitterProtocolId, setTwitterProtocolId] = useState('');
  const [twitterUpdate, setTwitterUpdate] = useState('');
  const [twitterInsight, setTwitterInsight] = useState('');
  const [twitterCustom, setTwitterCustom] = useState('');
  const [newsList, setNewsList] = useState([]);
  const [selectedNewsIndex, setSelectedNewsIndex] = useState(0);
  const [loadingNews, setLoadingNews] = useState(false);
  const [generatedTweet, setGeneratedTweet] = useState('');
  const [isThread, setIsThread] = useState(false);
  const [generatedThread, setGeneratedThread] = useState([]);
  const [twitterLoading, setTwitterLoading] = useState(false);
  const [twitterPosting, setTwitterPosting] = useState(false);
  const [twitterError, setTwitterError] = useState('');

  // News modal states
  const [isNewsModalOpen, setIsNewsModalOpen] = useState(false);
  const [newsProtocol, setNewsProtocol] = useState(null);
  const [protocolNews, setProtocolNews] = useState([]);
  const [newsLoading, setNewsLoading] = useState(false);
  const [newsError, setNewsError] = useState('');

  // Check if already authenticated (使用 token)
  useEffect(() => {
    const checkAuth = async () => {
      const savedToken = localStorage.getItem('admin_token');
      
      if (savedToken) {
        // 验证 token 是否有效
        try {
          const response = await fetch(`${WORKER_API}/admin/api/verify`, {
            headers: {
              'Authorization': `Bearer ${savedToken}`
            }
          });
          
            if (response.ok) {
            const result = await response.json();
            if (result.valid) {
              // Token 有效，自动登录
              setAuthToken(savedToken);
              setIsAuthenticated(true);
              loadData(1, '', 'tvl');
            } else {
              // Token 无效，清除
              localStorage.removeItem('admin_token');
          }
        } else {
            // 验证失败，清除
            localStorage.removeItem('admin_token');
          }
        } catch (err) {
          // 网络错误，清除 token
          localStorage.removeItem('admin_token');
        }
      }
    };
    
    checkAuth();
  }, []);

  const handleLogin = async (e) => {
    e.preventDefault();
    if (!email || !password) {
      setError('Please enter email and password');
      return;
    }
    
    setLoading(true);
    setError('');
    
    try {
      // 调用登录接口获取 token
      const response = await fetch(`${WORKER_API}/admin/api/login`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json'
        },
        body: JSON.stringify({ email, password })
      });
      
      const data = await response.json();
      
      if (response.ok && data.success && data.token) {
        // 登录成功，保存 token
        const token = data.token;
        console.log('[Login] Token received, length:', token.length);
        setAuthToken(token);
        setIsAuthenticated(true);
        localStorage.setItem('admin_token', token);
        setPassword(''); // 清除密码输入
        setError('');
        // 延迟加载数据，确保 token 已设置
        setTimeout(() => {
          loadData(1, '', 'tvl');
        }, 100);
      } else {
        // 登录失败
        console.error('[Login] Failed:', data);
        setError(data.error || 'Invalid password');
      }
    } catch (err) {
      setError('Network error. Please check your connection.');
    } finally {
      setLoading(false);
    }
  };

  const handleLogout = () => {
    setIsAuthenticated(false);
    localStorage.removeItem('admin_token');
    setAuthToken('');
    setEmail('');
    setPassword('');
    setProtocols([]);
    setPagination(null);
  };

  // 获取认证 token（用于 API 请求）
  const getAuthToken = () => {
    return authToken || localStorage.getItem('admin_token') || '';
  };
  
  // 创建带认证的 fetch 选项
  const getAuthHeaders = () => {
    const token = getAuthToken();
    return {
      'Content-Type': 'application/json',
      ...(token ? { 'Authorization': `Bearer ${token}` } : {})
    };
  };
  
  // 统一的认证 fetch 函数（处理 401 错误）
  const authFetch = async (url, options = {}) => {
    const token = getAuthToken();
    const headers = {
      ...getAuthHeaders(),
      ...(options.headers || {})
    };
    
    const response = await fetch(url, {
      ...options,
      headers
    });
    
    if (response.status === 401) {
      // Token 失效，清除并退出登录
      setIsAuthenticated(false);
      localStorage.removeItem('admin_token');
      setAuthToken('');
      setError('Session expired. Please login again.');
      throw new Error('Unauthorized');
    }
    
    return response;
  };

  const loadData = async (page, searchTerm, sort, applyFilters = false) => {
    setLoading(true);
    try {
      const url = new URL(`${WORKER_API}/admin/api/data`);
      url.searchParams.set('page', page);
      if (searchTerm) {
        url.searchParams.set('search', searchTerm);
      }
      if (sort) {
        url.searchParams.set('sortBy', sort);
      }
      
      // 如果应用筛选条件
      if (applyFilters) {
        if (minTvl && minTvl !== '') {
          url.searchParams.set('minTvl', minTvl);
        }
        if (maxTvl && maxTvl !== '') {
          url.searchParams.set('maxTvl', maxTvl);
        }
        if (selectedCategories.length > 0) {
          url.searchParams.set('categories', selectedCategories.join(','));
        }
        if (selectedBlockchains.length > 0) {
          url.searchParams.set('blockchains', selectedBlockchains.join(','));
        }
        if (selectedScores.length > 0) {
          url.searchParams.set('scores', selectedScores.join(','));
        }
      }

      const response = await authFetch(url);
      if (!response.ok) {
        // authFetch 已经处理了 401 错误
        // 其他错误不退出登录，只显示错误信息
        const errorData = await response.json().catch(() => ({}));
        setError(errorData.error || 'Failed to fetch data');
        return;
      }

      const data = await response.json();
      setProtocols(data.protocols || []);
      setPagination(data.pagination);
      setCurrentPage(page);
      
      // Extract available options for filters (从当前页数据中提取，如果选项为空)
      // 只在选项为空时更新，避免覆盖已有的完整选项列表
      if (data.protocols && data.protocols.length > 0) {
        const categories = [...new Set(data.protocols.map(p => p.category).filter(Boolean))].sort();
        const blockchains = [...new Set(data.protocols.map(p => p.blockchain).filter(Boolean))].sort();
        
        // 合并到现有选项（去重）
        setAvailableCategories(prev => {
          const combined = [...new Set([...prev, ...categories])].sort();
          return combined.length > 0 ? combined : prev;
        });
        setAvailableBlockchains(prev => {
          const combined = [...new Set([...prev, ...blockchains])].sort();
          return combined.length > 0 ? combined : prev;
        });
      }
    } catch (err) {
      setError('Failed to load data: ' + err.message);
    } finally {
      setLoading(false);
    }
  };

  const handleSearch = (e) => {
    e.preventDefault();
    loadData(1, search, sortBy, true);
  };

  const handlePageChange = (newPage) => {
    loadData(newPage, search, sortBy, password, true);
  };

  const handleSortChange = (newSort) => {
    setSortBy(newSort);
    loadData(1, search, newSort, password, true);
  };
  
  const handleApplyFilters = () => {
    // 使用 token 认证，不再需要 password
    console.log('Applying filters:', {
      minTvl,
      maxTvl,
      selectedCategories,
      selectedBlockchains,
      selectedScores
    });
    loadData(1, search, sortBy, true);
  };

  const toggleProtocolSelection = (protocolId) => {
    setSelectedProtocolIds(prev => {
      const newSet = new Set(prev);
      if (newSet.has(protocolId)) {
        newSet.delete(protocolId);
      } else {
        newSet.add(protocolId);
      }
      return newSet;
    });
  };

  const toggleSelectAll = () => {
    if (selectedProtocolIds.size === protocols.length) {
      setSelectedProtocolIds(new Set());
    } else {
      setSelectedProtocolIds(new Set(protocols.map(p => p.id)));
    }
  };

  const resetCreateForm = () => {
    setNewProtocolData(getEmptyProtocolForm());
    setCreateError('');
  };

  const handleCreateFieldChange = (field, value) => {
    setNewProtocolData(prev => ({
      ...prev,
      [field]: value
    }));
  };

  const handleCreateProtocol = async () => {
    const trimmedName = newProtocolData.name.trim();
    const trimmedChain = newProtocolData.blockchain.trim();

    if (!trimmedName || !trimmedChain) {
      setCreateError('名称和公链是必填项');
      return;
    }

    // 使用 token 认证，检查是否已登录
    if (!isAuthenticated) {
      setCreateError('请先登录');
      return;
    }

    setCreatingProtocol(true);
    setCreateError('');

    const parsedTvl = Number(newProtocolData.tvl);
    const payload = {
      ...newProtocolData,
      name: trimmedName,
      blockchain: trimmedChain,
      tvl: Number.isFinite(parsedTvl) ? parsedTvl : 0,
      hidden: !!newProtocolData.hidden,
      // 使用 token 认证，不再需要 password
    };

    try {
      const response = await authFetch(`${WORKER_API}/admin/api/create-protocol`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json'
        },
        body: JSON.stringify(payload)
      });

      if (!response.ok) {
        const errorData = await response.json().catch(() => ({}));
        throw new Error(errorData.error || '创建失败');
      }

      const result = await response.json();
      alert(result.message || '项目创建成功');
      resetCreateForm();
      setIsCreateModalOpen(false);
      setTimeout(() => {
        loadData(1, search, sortBy, true);
      }, 200);
    } catch (err) {
      setCreateError(err.message);
    } finally {
      setCreatingProtocol(false);
    }
  };

  const handleToggleVisibility = async (protocol) => {
    // 使用 token 认证，检查是否已登录
    if (!isAuthenticated) {
      setError('请先登录');
      return;
    }

    setVisibilityUpdatingId(protocol.id);
    try {
      const response = await authFetch(`${WORKER_API}/admin/api/toggle-protocol-visibility`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json'
        },
        body: JSON.stringify({
          // 使用 token 认证，不再需要 password,
          protocolId: protocol.id,
          hidden: !protocol.hidden
        })
      });

      if (!response.ok) {
        const errorData = await response.json().catch(() => ({}));
        throw new Error(errorData.error || '操作失败');
      }

      await response.json();
      setTimeout(() => {
        loadData(currentPage, search, sortBy, true);
      }, 200);
    } catch (err) {
      setError('切换显示状态失败: ' + err.message);
    } finally {
      setVisibilityUpdatingId(null);
    }
  };

  const handleAutoFillFromUrl = async () => {
    const targetUrl = (newProtocolData.url || '').trim();
    if (!targetUrl) {
      setCreateError('请先填写官网链接再使用 AI 自动填充');
      return;
    }

    // 使用 token 认证，检查是否已登录
    if (!isAuthenticated) {
      setCreateError('请先登录');
      return;
    }

    setAutoFillLoading(true);
    setCreateError('');

    try {
      const response = await authFetch(`${WORKER_API}/admin/api/protocol-autofill`, {
        method: 'POST',
        body: JSON.stringify({ url: targetUrl })
      });

      if (!response.ok) {
        const errorData = await response.json().catch(() => ({}));
        throw new Error(errorData.error || 'AI 自动填充失败');
      }

      const result = await response.json();
      const suggestion = result?.suggestion;
      if (suggestion) {
        setNewProtocolData(prev => {
          const nextTvlValue = suggestion.tvl ?? prev.tvl;
          return {
            ...prev,
            name: suggestion.name || prev.name,
            blockchain: suggestion.blockchain || prev.blockchain,
            category: suggestion.category || prev.category,
            description: suggestion.description || prev.description,
            tvl: nextTvlValue === undefined || nextTvlValue === null ? '' : String(nextTvlValue),
            url: suggestion.url || prev.url,
            logo: suggestion.logo || prev.logo,
            slug: suggestion.slug || prev.slug,
            hidden: typeof suggestion.hidden === 'boolean' ? suggestion.hidden : prev.hidden
          };
        });
      }
    } catch (err) {
      setCreateError('AI 自动填充失败: ' + err.message);
    } finally {
      setAutoFillLoading(false);
    }
  };

  const handleGenerateFaviconLogo = async (protocol) => {
    // 使用 token 认证，检查是否已登录
    if (!isAuthenticated) {
      setError('请先登录');
      return;
    }
    if (!protocol?.url) {
      setError('该项目没有官网链接，无法抓取 favicon');
      return;
    }

    setFaviconUpdatingId(protocol.id);
    setUpdateLogs(prev => [
      ...prev,
      { time: new Date().toLocaleTimeString(), message: `开始抓取 ${protocol.name} 的 favicon...` }
    ]);
    try {
      const response = await authFetch(`${WORKER_API}/admin/api/generate-favicon-logo`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json'
        },
        body: JSON.stringify({
          // 使用 token 认证，不再需要 password,
          protocolId: protocol.id,
          url: protocol.url
        })
      });

      if (!response.ok) {
        const errorData = await response.json().catch(() => ({}));
        throw new Error(errorData.error || '抓取失败');
      }

      const result = await response.json();
      setUpdateLogs(prev => [
        ...prev,
        { time: new Date().toLocaleTimeString(), message: `已更新 ${protocol.name} 的 Logo` }
      ]);
      alert(result.message || 'Logo 已更新');
      setTimeout(() => {
        loadData(currentPage, search, sortBy, true);
      }, 300);
    } catch (err) {
      setError('获取 favicon 失败: ' + err.message);
      setUpdateLogs(prev => [
        ...prev,
        { time: new Date().toLocaleTimeString(), message: `获取 favicon 失败：${err.message}` }
      ]);
    } finally {
      setFaviconUpdatingId(null);
    }
  };

  const closeCreateModal = () => {
    if (creatingProtocol) return;
    setIsCreateModalOpen(false);
    setCreateError('');
    resetCreateForm();
  };

  const formatTvl = (tvl) => {
    if (!tvl) return '—';
    if (tvl >= 1e9) return `$${(tvl / 1e9).toFixed(2)}B`;
    if (tvl >= 1e6) return `$${(tvl / 1e6).toFixed(2)}M`;
    if (tvl >= 1e3) return `$${(tvl / 1e3).toFixed(2)}K`;
    return `$${tvl.toFixed(2)}`;
  };

  const formatApyValue = (value) => {
    if (value === null || value === undefined || Number.isNaN(Number(value))) {
      return '—';
    }
    const num = Number(value);
    if (Math.abs(num) >= 1000) return `${num.toFixed(0)}%`;
    if (Math.abs(num) >= 100) return `${num.toFixed(1)}%`;
    return `${num.toFixed(2)}%`;
  };

  const getProtocolApyData = (protocol) => {
    if (!protocol) {
      return { total: '—', base: '—', reward: '—', updated: null };
    }

    const apyBase = typeof protocol.apyBase === 'number' ? protocol.apyBase : null;
    const apyReward = typeof protocol.apyReward === 'number' ? protocol.apyReward : null;
    const provided = typeof protocol.apy === 'number' ? protocol.apy : null;
    const combined =
      provided !== null
        ? provided
        : apyBase !== null && apyReward !== null
        ? apyBase + apyReward
        : apyBase ?? apyReward;

    return {
      total: formatApyValue(combined),
      base: formatApyValue(apyBase),
      reward: formatApyValue(apyReward),
      updated: protocol.apyUpdatedAt ? formatTimeAgo(protocol.apyUpdatedAt) : null,
    };
  };

  const formatTimeAgo = (timestamp) => {
    if (!timestamp) return 'Never';
    try {
      const date = new Date(timestamp);
      if (isNaN(date.getTime())) return 'Invalid';
      const now = new Date();
      const diffInSeconds = Math.floor((now - date) / 1000);
      if (diffInSeconds < 60) return 'just now';
      const diffInMinutes = Math.floor(diffInSeconds / 60);
      if (diffInMinutes < 60) return `${diffInMinutes}m ago`;
      const diffInHours = Math.floor(diffInMinutes / 60);
      if (diffInHours < 24) return `${diffInHours}h ago`;
      const diffInDays = Math.floor(diffInHours / 24);
      if (diffInDays < 30) return `${diffInDays}d ago`;
      const diffInMonths = Math.floor(diffInDays / 30);
      if (diffInMonths < 12) return `${diffInMonths}mo ago`;
      return `${Math.floor(diffInMonths / 12)}y ago`;
    } catch {
      return 'Unknown';
    }
  };

  const handleUpdateList = async () => {
    // 使用 token 认证，不再需要 password
    setUpdateMode('list');
    
    try {
      // 先获取待更新列表
      const listParams = {
        // 使用 token 认证，不再需要 password,
        minTvl: minTvl && minTvl !== '' ? parseFloat(minTvl) : null,
        maxTvl: maxTvl && maxTvl !== '' ? parseFloat(maxTvl) : null,
        categories: selectedCategories.length > 0 ? selectedCategories : [],
        blockchains: selectedBlockchains.length > 0 ? selectedBlockchains : [],
        scores: selectedScores.length > 0 ? selectedScores.map(s => String(s)) : []
      };
      
      console.log('Sending filter params:', listParams);

      const listResponse = await authFetch(`${WORKER_API}/admin/api/update-list`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
        },
        body: JSON.stringify(listParams),
      });

      if (!listResponse.ok) {
        if (listResponse.status === 401) {
          setIsAuthenticated(false);
          sessionStorage.removeItem('admin_password');
          setError('Session expired. Please login again.');
          return;
        }
        const errorData = await listResponse.json();
        throw new Error(errorData.error || 'Failed to get update list');
      }

      const listData = await listResponse.json();
      
      if (listData.count === 0) {
        setError('No protocols match the filter criteria');
        return;
      }

      // 显示确认对话框
      setPendingUpdateList(listData.protocols);
      setShowConfirmDialog(true);
    } catch (err) {
      setError('Failed to get update list: ' + err.message);
    }
  };

  const handleUpdateSelected = async () => {
    if (selectedProtocolIds.size === 0) {
      setError('Please select at least one protocol to update');
      return;
    }

    // 使用 token 认证，不再需要 password
    setUpdateMode('selected');
    
    // 从当前页的 protocols 中获取选中的项目
    const selectedProtocols = protocols.filter(p => selectedProtocolIds.has(p.id));
    
    if (selectedProtocols.length === 0) {
      setError('No selected protocols found on current page');
      return;
    }

    // 显示确认对话框
    setPendingUpdateList(selectedProtocols);
    setShowConfirmDialog(true);
  };

  const handleClearScores = async (mode) => {
    // 使用 token 认证，不再需要 password
    
    let clearParams = {
      // 使用 token 认证，不再需要 password
    };
    
    if (mode === 'selected') {
      if (selectedProtocolIds.size === 0) {
        setError('Please select at least one protocol to clear scores');
        return;
      }
      clearParams.ids = Array.from(selectedProtocolIds).map(id => String(id));
    } else {
      // 使用筛选条件
      clearParams.minTvl = minTvl && minTvl !== '' ? parseFloat(minTvl) : null;
      clearParams.maxTvl = maxTvl && maxTvl !== '' ? parseFloat(maxTvl) : null;
      clearParams.categories = selectedCategories.length > 0 ? selectedCategories : [];
      clearParams.blockchains = selectedBlockchains.length > 0 ? selectedBlockchains : [];
      clearParams.scores = selectedScores.length > 0 ? selectedScores.map(s => String(s)) : [];
    }
    
    if (!window.confirm(`Are you sure you want to clear scores for ${mode === 'selected' ? selectedProtocolIds.size : 'filtered'} protocol(s)? This action cannot be undone.`)) {
      return;
    }
    
    setLoading(true);
    setError('');
    
    try {
      const response = await authFetch(`${WORKER_API}/admin/api/clear-scores`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
        },
        body: JSON.stringify(clearParams),
      });

      if (!response.ok) {
        if (response.status === 401) {
          setIsAuthenticated(false);
          localStorage.removeItem('admin_token');
          setAuthToken('');
          setError('Session expired. Please login again.');
          return;
        }
        const errorData = await response.json().catch(() => ({}));
        throw new Error(errorData.error || 'Failed to clear scores');
      }

      const result = await response.json();
      setError('');
      
      // 显示成功消息
      alert(`Successfully cleared scores for ${result.cleared} protocol(s)`);
      
      // 重新加载数据
      // 使用 token 认证，不再需要 password
      loadData(currentPage, search, sortBy);
      
      // 清除选中状态
      if (mode === 'selected') {
        setSelectedProtocolIds(new Set());
      }
    } catch (err) {
      setError('Failed to clear scores: ' + err.message);
    } finally {
      setLoading(false);
    }
  };

  const handleConfirmUpdate = async () => {
    setShowConfirmDialog(false);
    setUpdating(true);
    setUpdateLogs([]);
    setUpdateStatus('Starting update...');
    setRetryCount(0);
    setIsRetrying(false);
    
    // 使用 token 认证，不再需要 password
    
    try {
      let updateParams;
      
      if (updateMode === 'selected') {
        // 更新选中项目：使用 ID 列表
        const ids = Array.from(selectedProtocolIds).map(id => String(id));
        updateParams = {
          // 使用 token 认证，不再需要 password,
          ids: ids.join(','),
          version: aiVersion
        };
      } else {
        // 更新列表：使用筛选条件
        updateParams = {
          // 使用 token 认证，不再需要 password,
          minTvl: minTvl && minTvl !== '' ? parseFloat(minTvl) : null,
          maxTvl: maxTvl && maxTvl !== '' ? parseFloat(maxTvl) : null,
          categories: selectedCategories.length > 0 ? selectedCategories : [],
          blockchains: selectedBlockchains.length > 0 ? selectedBlockchains : [],
          scores: selectedScores.length > 0 ? selectedScores.map(s => String(s)) : [],
          version: aiVersion
        };
      }

      // 添加日志
      const addLog = (message) => {
        setUpdateLogs(prev => [...prev, { time: new Date().toLocaleTimeString(), message }]);
      };

      addLog(`Found ${pendingUpdateList.length} protocols to update`);
      addLog('Starting update process...');

      const response = await authFetch(`${WORKER_API}/admin/api/update`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
        },
        body: JSON.stringify(updateParams),
      });

      if (!response.ok) {
        if (response.status === 401) {
          setIsAuthenticated(false);
          localStorage.removeItem('admin_token');
          setAuthToken('');
          setError('Session expired. Please login again.');
          return;
        }
        const errorData = await response.json().catch(() => ({}));
        setError('Update failed: ' + (errorData.error || 'Unknown error'));
        setUpdating(false);
        setUpdateStatus('');
        setUpdateLogs(prev => [...prev, { time: new Date().toLocaleTimeString(), message: `Error: ${errorData.error || 'Unknown error'}` }]);
        return;
      }

      // 检查是否是流式响应
      const contentType = response.headers.get('content-type');
      if (contentType && contentType.includes('text/event-stream')) {
        // 流式响应：使用 EventSource 或手动解析 SSE
        const reader = response.body.getReader();
        const decoder = new TextDecoder();
        let buffer = '';
        
        while (true) {
          const { done, value } = await reader.read();
          if (done) break;
          
          buffer += decoder.decode(value, { stream: true });
          const lines = buffer.split('\n');
          buffer = lines.pop() || ''; // 保留最后不完整的行
          
          for (const line of lines) {
            if (line.startsWith('data: ')) {
              try {
                const data = JSON.parse(line.slice(6));
                
                if (data.type === 'start') {
                  addLog(`Starting update of ${data.total} protocol(s)...`);
                } else if (data.type === 'progress') {
                  addLog(`[${data.current}/${data.total}] Processing ${data.protocol}...`);
                } else if (data.type === 'result') {
                  if (data.success) {
                    const score = data.score !== null && data.score !== undefined ? data.score : 'N/A';
                    addLog(`[${data.current}/${data.total}] ✓ ${data.protocol_name} updated successfully (Score: ${score}, Grade: ${data.grade || 'N/A'})`);
                  } else {
                    addLog(`[${data.current}/${data.total}] ✗ ${data.protocol_name} failed: ${data.error || 'Unknown error'}`);
                  }
                } else if (data.type === 'complete') {
                  addLog(`\nUpdate completed: ${data.success} succeeded, ${data.failed} failed out of ${data.total} total`);
                  setUpdateStatus(`Update completed: ${data.success} succeeded, ${data.failed} failed out of ${data.total} total`);
                  
                  // 重置重试计数
                  setRetryCount(0);
                  setIsRetrying(false);
                  
                  // Reload data after update
                  setTimeout(() => {
                    // 使用 token 认证，不再需要 password
                    loadData(currentPage, search, sortBy);
                    setUpdateStatus('');
                    setUpdateLogs([]);
                    // 清除选中状态
                    if (updateMode === 'selected') {
                      setSelectedProtocolIds(new Set());
                    }
                  }, 3000);
                }
              } catch (e) {
                console.error('Failed to parse SSE data:', e, line);
              }
            }
          }
        }
      } else {
        // 非流式响应（兼容旧版本）
        const result = await response.json();
        
        // 解析结果并添加日志
        if (result.protocols && Array.isArray(result.protocols)) {
          result.protocols.forEach((protocolInfo, index) => {
            const protocolName = protocolInfo.protocol_name || pendingUpdateList[index]?.name || 'Unknown';
            const score = protocolInfo.score !== null && protocolInfo.score !== undefined ? protocolInfo.score : 'N/A';
            addLog(`[${index + 1}/${result.total || pendingUpdateList.length}] ${protocolName} updated successfully (Score: ${score})`);
          });
        } else {
          // 如果没有详细协议信息，至少显示总数
          addLog(`Update completed: ${result.updated || 0} protocol(s) updated`);
        }
        
        addLog(`Update completed: ${result.updated || pendingUpdateList.length} protocol(s) updated successfully`);
        setUpdateStatus(`Update completed: ${result.updated || pendingUpdateList.length} protocol(s) updated successfully`);
        
        // 重置重试计数
        setRetryCount(0);
        setIsRetrying(false);
        
        // Reload data after update
        setTimeout(() => {
          // 使用 token 认证，不再需要 password
          loadData(currentPage, search, sortBy);
          setUpdateStatus('');
          setUpdateLogs([]);
          // 清除选中状态
          if (updateMode === 'selected') {
            setSelectedProtocolIds(new Set());
          }
        }, 3000);
      }
    } catch (err) {
      const isNetworkError = err.message.includes('network') || 
                            err.message.includes('Network') || 
                            err.message.includes('fetch') ||
                            err.message.includes('Failed to fetch') ||
                            !navigator.onLine;
      
      if (isNetworkError && retryCount < 5) {
        // 网络错误，等待10秒后重试
        const newRetryCount = retryCount + 1;
        setRetryCount(newRetryCount);
        setIsRetrying(true);
        setUpdateStatus(`Network error detected. Retrying in 10 seconds... (Attempt ${newRetryCount}/5)`);
        setUpdateLogs(prev => [...prev, { 
          time: new Date().toLocaleTimeString(), 
          message: `Network error: ${err.message}. Retrying in 10 seconds... (Attempt ${newRetryCount}/5)` 
        }]);
        
        // 等待10秒后重试
        setTimeout(() => {
          setIsRetrying(false);
          setUpdateLogs(prev => [...prev, { 
            time: new Date().toLocaleTimeString(), 
            message: `Retrying update...` 
          }]);
          // 递归调用重试
          handleConfirmUpdate();
        }, 10000);
      } else {
        // 非网络错误或重试次数已用完
        setError('Update failed: ' + err.message);
        setUpdateStatus('');
        setUpdateLogs(prev => [...prev, { 
          time: new Date().toLocaleTimeString(), 
          message: `Error: ${err.message}${retryCount >= 5 ? ' (Max retries reached)' : ''}` 
        }]);
        setRetryCount(0);
        setIsRetrying(false);
        setUpdating(false);
      }
    } finally {
      if (!isRetrying) {
        setUpdating(false);
      }
    }
  };

  const toggleCategory = (category) => {
    setSelectedCategories(prev => 
      prev.includes(category) 
        ? prev.filter(c => c !== category)
        : [...prev, category]
    );
  };

  const toggleBlockchain = (blockchain) => {
    setSelectedBlockchains(prev => 
      prev.includes(blockchain) 
        ? prev.filter(b => b !== blockchain)
        : [...prev, blockchain]
    );
  };

  const toggleScore = (score) => {
    setSelectedScores(prev => 
      prev.includes(score) 
        ? prev.filter(s => s !== score)
        : [...prev, score]
    );
  };

  const handleNewsClick = async (protocol) => {
    if (!protocol) return;

    setNewsProtocol(protocol);
    setIsNewsModalOpen(true);
    setNewsLoading(true);
    setNewsError('');
    setProtocolNews([]);

    try {
      const url = new URL(`${WORKER_API}/admin/api/news/by-protocol`);
      url.searchParams.set('protocolId', protocol.id);
      
      const response = await authFetch(url);
      if (!response.ok) {
        throw new Error('Failed to fetch news');
      }
      
      const data = await response.json();
      if (data.success) {
        setProtocolNews(data.news || []);
      } else {
        throw new Error(data.error || 'Failed to fetch news');
      }
    } catch (err) {
      setNewsError(err.message || 'Failed to load news');
      console.error('Error loading news:', err);
    } finally {
      setNewsLoading(false);
    }
  };

  const handleScoreClick = async (protocol) => {
    if (!protocol || typeof protocol.score !== 'number') return;

    setSelectedProject(protocol);
    setIsReportModalOpen(true);
    setReportLoading(true);
    setReportError(null);
    setSelectedReport(null);
    setHistoryData([]);
    setHistoryData([]);

    try {
      // 获取当前报告
      const currentReport = await fetchProtocolScore(protocol.id);
      setSelectedReport(currentReport);
      
      // 获取历史报告列表
      // 使用 token 认证，不再需要 password
      const url = new URL(`${WORKER_API}/admin/api/history`);
      // 使用 token 认证，不再需要 password
      url.searchParams.set('protocolId', protocol.id);
      
      const response = await authFetch(url);
      if (!response.ok) {
        throw new Error('Failed to fetch history');
      }
      
      const data = await response.json();
      console.log('[Frontend] History API response:', {
        protocolId: protocol.id,
        historyCount: data.history?.length || 0,
        history: data.history
      });
      
      // 将所有报告（当前+历史）合并，统一处理
      const allReports = [];
      
      // 添加当前报告
      if (currentReport) {
        allReports.push({
          ...currentReport,
          isCurrent: true,
          historyKey: null, // 当前报告没有 historyKey
          timestamp: protocol.lastScoreUpdatedAt || currentReport.timestamp
        });
        console.log('[Frontend] Added current report:', {
          timestamp: protocol.lastScoreUpdatedAt || currentReport.timestamp,
          score: currentReport?.overall_risk?.risk_score
        });
      }
      
      // 添加历史报告
      if (data.history && Array.isArray(data.history)) {
        console.log('[Frontend] Adding history reports:', data.history.length);
        allReports.push(...data.history.map(h => ({ 
          ...h, 
          isCurrent: false,
          historyKey: h.historyKey || h.key
        })));
      } else {
        console.log('[Frontend] No history reports found');
      }
      
      // 按时间倒序排列
      const sortedReports = allReports.sort((a, b) => new Date(b.timestamp) - new Date(a.timestamp));
      console.log('[Frontend] Total reports (current + history):', sortedReports.length);
      setHistoryData(sortedReports);
    } catch (err) {
      setReportError('Unable to load reports: ' + err.message);
    } finally {
      setReportLoading(false);
    }
  };

  const closeReportModal = () => {
    setIsReportModalOpen(false);
    setSelectedProject(null);
    setSelectedReport(null);
    setReportError(null);
  };

  const loadConfig = async () => {
    try {
      // 使用 token 认证，不再需要 password
      const response = await authFetch(`${WORKER_API}/admin/api/config`);
      if (!response.ok) {
        throw new Error('Failed to load config');
      }
      const data = await response.json();
      if (data.success && data.config) {
        setConfigData({
          notificationEmail: data.config.notificationEmail || '',
          updateInterval: data.config.updateInterval || '0 * * * *',
          twitterAutoPostEnabled: data.config.twitterAutoPostEnabled !== undefined ? data.config.twitterAutoPostEnabled : false,
          scoreAutoUpdateEnabled: data.config.scoreAutoUpdateEnabled !== undefined ? data.config.scoreAutoUpdateEnabled : false,
          scoreUpdateIntervalHigh: data.config.scoreUpdateIntervalHigh !== undefined ? data.config.scoreUpdateIntervalHigh : 7 * 24,
          scoreUpdateIntervalMedium: data.config.scoreUpdateIntervalMedium !== undefined ? data.config.scoreUpdateIntervalMedium : 14 * 24,
          scoreUpdateIntervalLow: data.config.scoreUpdateIntervalLow !== undefined ? data.config.scoreUpdateIntervalLow : 30 * 24
        });
        if (data.twitterAutoPostStatus) {
          setTwitterAutoPostStatus(data.twitterAutoPostStatus);
        }
      }
    } catch (err) {
      setError('Failed to load config: ' + err.message);
    }
  };

  const handleSaveConfig = async () => {
    setConfigLoading(true);
    try {
      // 使用 token 认证，不再需要 password
      const response = await authFetch(`${WORKER_API}/admin/api/config`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
        },
        body: JSON.stringify({
          // 使用 token 认证，不再需要 password,
          notificationEmail: configData.notificationEmail,
          updateInterval: configData.updateInterval,
          twitterAutoPostEnabled: configData.twitterAutoPostEnabled,
          scoreAutoUpdateEnabled: configData.scoreAutoUpdateEnabled,
          scoreUpdateIntervalHigh: configData.scoreUpdateIntervalHigh,
          scoreUpdateIntervalMedium: configData.scoreUpdateIntervalMedium,
          scoreUpdateIntervalLow: configData.scoreUpdateIntervalLow
        })
      });

      if (!response.ok) {
        const errorData = await response.json();
        throw new Error(errorData.error || 'Failed to save config');
      }

      const result = await response.json();
      if (result.success) {
        setError('');
        // 重新加载配置以获取最新的自动发推状态
        await loadConfig();
        setIsConfigModalOpen(false);
        alert('Configuration saved successfully!');
      }
    } catch (err) {
      setError('Failed to save config: ' + err.message);
    } finally {
      setConfigLoading(false);
    }
  };

  const closeConfigModal = () => {
    setIsConfigModalOpen(false);
    setConfigData({
      notificationEmail: '',
      updateInterval: '0 * * * *',
      twitterAutoPostEnabled: false,
      scoreAutoUpdateEnabled: false,
      scoreUpdateIntervalHigh: 7 * 24,
      scoreUpdateIntervalMedium: 14 * 24,
      scoreUpdateIntervalLow: 30 * 24
    });
  };

  // Twitter functions
  const handleGenerateTweet = async () => {
    // 根据推文类型验证输入
    if (twitterTweetType === 'protocol_analysis' && !twitterProtocolId) {
      setTwitterError('Please enter a protocol ID');
      return;
    }
    if (twitterTweetType === 'platform_update' && !twitterUpdate.trim()) {
      setTwitterError('Please enter platform update content');
      return;
    }
    if (twitterTweetType === 'industry_insight' && !twitterInsight.trim()) {
      setTwitterError('Please enter industry insight content');
      return;
    }
    if (twitterTweetType === 'news_alert' && newsList.length === 0) {
      setTwitterError('Please load and select a news item');
      return;
    }
    if (twitterTweetType === 'daily_brief' && newsList.length === 0) {
      // daily_brief 会自动获取新闻，但也可以手动选择
      // 这里不强制要求，让后端自动获取
    }
    if (twitterTweetType === 'custom' && !twitterCustom.trim()) {
      setTwitterError('Please enter custom tweet content');
      return;
    }

    setTwitterLoading(true);
    setTwitterError('');
    setGeneratedTweet('');
    setGeneratedThread([]);
    setIsThread(false);

    try {
      // 使用 token 认证，不再需要 password
      const requestBody = {
        // 使用 token 认证，不再需要 password,
        tweetType: twitterTweetType
      };

      // 根据类型添加相应的参数
      if (twitterTweetType === 'protocol_analysis') {
        requestBody.protocolId = twitterProtocolId;
      } else if (twitterTweetType === 'platform_update') {
        requestBody.update = twitterUpdate;
      } else if (twitterTweetType === 'industry_insight') {
        requestBody.insight = twitterInsight;
      } else if (twitterTweetType === 'news_alert') {
        if (newsList.length > 0 && newsList[selectedNewsIndex]) {
          requestBody.news = newsList[selectedNewsIndex];
        }
      } else if (twitterTweetType === 'daily_brief') {
        // 如果前端有选中的新闻列表，传给后端；否则后端自动获取
        if (newsList.length > 0) {
          requestBody.newsList = newsList;
        }
      } else if (twitterTweetType === 'daily_headlines') {
        // daily_headlines 会自动获取新闻，不需要传参数
        // 如果前端有选中的新闻列表，可以传给后端；否则后端自动获取
        if (newsList.length > 0) {
          requestBody.newsList = newsList;
        }
      } else if (twitterTweetType === 'custom') {
        requestBody.custom = twitterCustom;
      }

      const response = await authFetch(`${WORKER_API}/admin/api/twitter/generate`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
        },
        body: JSON.stringify(requestBody)
      });

      if (!response.ok) {
        const errorData = await response.json();
        throw new Error(errorData.error || 'Failed to generate tweet');
      }

      const data = await response.json();
      if (data.isThread && Array.isArray(data.tweet)) {
        setIsThread(true);
        setGeneratedThread(data.tweet);
        setGeneratedTweet(''); // 清空单条推文
      } else {
        setIsThread(false);
        setGeneratedThread([]);
      setGeneratedTweet(data.tweet || '');
      }
    } catch (err) {
      setTwitterError(err.message || 'Failed to generate tweet');
    } finally {
      setTwitterLoading(false);
    }
  };

  const handlePostTweet = async () => {
    if (isThread) {
      if (generatedThread.length === 0) {
        setTwitterError('Please generate a thread first');
        return;
      }
    } else {
    if (!generatedTweet.trim()) {
      setTwitterError('Please generate a tweet first');
      return;
      }
    }

    setTwitterPosting(true);
    setTwitterError('');

    try {
      const requestBody = isThread
        ? { tweetArray: generatedThread }
        : { tweet: generatedTweet };

      const response = await authFetch(`${WORKER_API}/admin/api/twitter/post`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
        },
        body: JSON.stringify(requestBody)
      });

      if (!response.ok) {
        const errorData = await response.json();
        throw new Error(errorData.error || 'Failed to post tweet');
      }

      const data = await response.json();
      if (data.isThread) {
        alert(`Thread posted successfully! First Tweet ID: ${data.firstTweetId} (${data.threadLength} tweets)`);
        setGeneratedThread([]);
      } else {
      alert(`Tweet posted successfully! Tweet ID: ${data.tweetId}`);
      setGeneratedTweet('');
      }
      setTwitterProtocolId('');
      setIsThread(false);
    } catch (err) {
      setTwitterError(err.message || 'Failed to post tweet');
    } finally {
      setTwitterPosting(false);
    }
  };

  const loadNewsList = async () => {
    setLoadingNews(true);
    setTwitterError('');
    try {
      const response = await authFetch(`${WORKER_API}/admin/api/news/list`, {
        method: 'GET'
      });

      if (!response.ok) {
        const errorData = await response.json().catch(() => ({}));
        throw new Error(errorData.error || `HTTP ${response.status}: Failed to load news`);
      }

      const data = await response.json();
      if (data.success !== false) {
        // 即使 news 为空也处理
        const news = data.news || [];
        const newsWithHours = news.map(newsItem => {
          try {
            const publishedTime = new Date(newsItem.published_at || newsItem.created_at).getTime();
            const hoursAgo = isNaN(publishedTime) ? 0 : Math.floor((Date.now() - publishedTime) / (1000 * 60 * 60));
            return { ...newsItem, hoursAgo };
          } catch (e) {
            return { ...newsItem, hoursAgo: 0 };
          }
        });
        setNewsList(newsWithHours);
        if (newsWithHours.length > 0) {
          setSelectedNewsIndex(0);
        } else {
          setTwitterError('No recent news found. This might be due to API limitations or no recent DeFi news.');
        }
      } else {
        throw new Error(data.error || 'Failed to load news');
      }
    } catch (err) {
      console.error('Error loading news:', err);
      setTwitterError('Failed to load news: ' + (err.message || 'Unknown error'));
      setNewsList([]);
    } finally {
      setLoadingNews(false);
    }
  };

  const closeTwitterModal = () => {
    setIsTwitterModalOpen(false);
    setTwitterTweetType('protocol_analysis');
    setTwitterProtocolId('');
    setTwitterUpdate('');
    setTwitterInsight('');
    setTwitterCustom('');
    setGeneratedTweet('');
    setTwitterError('');
    setNewsList([]);
    setSelectedNewsIndex(0);
  };

  const handleHistoryClick = async (protocol) => {
    setHistoryProtocol(protocol);
    setIsHistoryModalOpen(true);
    setHistoryLoading(true);
    setHistoryData([]);

    try {
      // 使用 token 认证，不再需要 password
      const url = new URL(`${WORKER_API}/admin/api/history`);
      // 使用 token 认证，不再需要 password
      url.searchParams.set('protocolId', protocol.id);
      
      const response = await authFetch(url);
      if (!response.ok) {
        throw new Error('Failed to fetch history');
      }
      
      const data = await response.json();
      setHistoryData(data.history || []);
    } catch (err) {
      console.error('Failed to fetch history:', err);
      setError('Failed to load history: ' + err.message);
    } finally {
      setHistoryLoading(false);
    }
  };

  const handleDeleteHistory = async (historyKey, isCurrent = false) => {
    const protocolId = selectedProject?.id || historyProtocol?.id;
    
    if (isCurrent) {
      // 删除当前报告 = 清空分数
      if (!window.confirm('Are you sure you want to clear the current score? This will remove the score and report.')) {
        return;
      }
      
      try {
        // 使用 token 认证，不再需要 password
        const response = await authFetch(`${WORKER_API}/admin/api/clear-scores`, {
          method: 'POST',
          headers: {
            'Content-Type': 'application/json',
          },
          body: JSON.stringify({
            // 使用 token 认证，不再需要 password,
            ids: [String(protocolId)]
          }),
        });

        if (!response.ok) {
          if (response.status === 401) {
            setIsAuthenticated(false);
            localStorage.removeItem('admin_password');
            localStorage.removeItem('admin_login_time');
            setError('Session expired. Please login again.');
            return;
          }
          throw new Error('Failed to clear score');
        }

        // 关闭模态框并重新加载数据
        closeReportModal();
        // 使用 token 认证，不再需要 password
        loadData(currentPage, search, sortBy);
      } catch (err) {
        setError('Failed to clear score: ' + err.message);
      }
    } else {
      // 删除历史报告
      if (!window.confirm('Are you sure you want to delete this history report?')) {
        return;
      }

      try {
        // 使用 token 认证，不再需要 password
        const response = await authFetch(`${WORKER_API}/admin/api/delete-history`, {
          method: 'POST',
          headers: {
            'Content-Type': 'application/json',
          },
          body: JSON.stringify({
            // 使用 token 认证，不再需要 password,
            protocolId: protocolId,
            historyKey: historyKey
          }),
        });

        if (!response.ok) {
          if (response.status === 401) {
            setIsAuthenticated(false);
            localStorage.removeItem('admin_password');
            localStorage.removeItem('admin_login_time');
            setError('Session expired. Please login again.');
            return;
          }
          throw new Error('Failed to delete history');
        }

        // 重新加载历史数据
        if (selectedProject) {
          await handleScoreClick(selectedProject);
        } else if (historyProtocol) {
          await handleHistoryClick(historyProtocol);
        }
      } catch (err) {
        setError('Failed to delete history: ' + err.message);
      }
    }
  };

  const closeHistoryModal = () => {
    setIsHistoryModalOpen(false);
    setHistoryProtocol(null);
    setHistoryData([]);
  };

  if (!isAuthenticated) {
    return (
      <div className="min-h-screen bg-gray-50 flex items-center justify-center">
        <div className="bg-white rounded-xl shadow-lg p-8 w-full max-w-md">
          <h1 className="text-2xl font-bold text-gray-900 mb-6 text-center">Admin Login</h1>
          <form onSubmit={handleLogin}>
            <div className="mb-4">
              <label htmlFor="email" className="block text-sm font-medium text-gray-700 mb-2">
                Email
              </label>
              <input
                type="email"
                id="email"
                value={email}
                onChange={(e) => setEmail(e.target.value)}
                className="w-full px-4 py-2 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-blue-500"
                placeholder="admin@example.com"
                required
                autoFocus
              />
            </div>
            <div className="mb-4">
              <label htmlFor="password" className="block text-sm font-medium text-gray-700 mb-2">
                Password
              </label>
              <input
                type="password"
                id="password"
                value={password}
                onChange={(e) => setPassword(e.target.value)}
                className="w-full px-4 py-2 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-blue-500"
                required
              />
            </div>
            {error && (
              <div className="mb-4 text-red-600 text-sm">{error}</div>
            )}
            <button
              type="submit"
              className="w-full bg-blue-600 text-white py-2 px-4 rounded-lg hover:bg-blue-700 font-medium"
            >
              Login
            </button>
          </form>
        </div>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-gray-50">
      <div className="bg-white shadow-sm border-b">
        <div className="w-full px-4 py-4">
          <div className="flex items-center justify-between">
            <h1 className="text-2xl font-bold text-gray-900">Admin Panel</h1>
            <button
              onClick={handleLogout}
              className="px-4 py-2 bg-red-600 text-white rounded-lg hover:bg-red-700"
            >
              Logout
            </button>
          </div>
        </div>
      </div>

      <div className="w-full px-4 py-6">
        {/* Search and Sort */}
        <div className="bg-white rounded-xl border border-gray-200 p-4 mb-6 shadow-sm">
          <div className="flex flex-wrap gap-4 items-end">
            <form onSubmit={handleSearch} className="flex-1 min-w-[300px]">
              <label className="block text-xs font-semibold text-gray-600 uppercase tracking-wide mb-2">
                Search
              </label>
              <div className="flex gap-2">
                <input
                  type="text"
                  value={search}
                  onChange={(e) => setSearch(e.target.value)}
                  placeholder="Search by name, category, blockchain..."
                  className="flex-1 px-4 py-2 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-blue-500"
                />
                <button
                  type="submit"
                  className="px-6 py-2 bg-blue-600 text-white rounded-lg hover:bg-blue-700"
                >
                  Search
                </button>
              </div>
            </form>
            <div className="min-w-[150px]">
              <label className="block text-xs font-semibold text-gray-600 uppercase tracking-wide mb-2">
                Sort By
              </label>
              <select
                value={sortBy}
                onChange={(e) => handleSortChange(e.target.value)}
                className="w-full px-4 py-2 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-blue-500"
              >
                <option value="tvl">TVL</option>
                <option value="score">Credit Score</option>
              </select>
            </div>
            <div>
              <button
                onClick={() => setShowFilters(!showFilters)}
                className="px-6 py-2 bg-gray-600 text-white rounded-lg hover:bg-gray-700"
              >
                {showFilters ? 'Hide Filters' : 'Show Filters'}
              </button>
            </div>
            <div className="flex gap-2 items-center">
              <div className="flex items-center gap-2">
                <label className="text-sm font-medium text-gray-700">AI Version:</label>
                <select
                  value={aiVersion}
                  onChange={(e) => setAiVersion(e.target.value)}
                  disabled={updating}
                  className="px-3 py-2 border border-gray-300 rounded-lg bg-white text-sm focus:outline-none focus:ring-2 focus:ring-blue-500 disabled:opacity-50 disabled:cursor-not-allowed"
                >
                  <option value="2.0">2.0 (Advanced)</option>
                  <option value="1.0">1.0 (Legacy)</option>
                </select>
              </div>
              <button
                onClick={handleUpdateList}
                disabled={updating || loading}
                className="px-6 py-2 bg-green-600 text-white rounded-lg hover:bg-green-700 disabled:opacity-50 disabled:cursor-not-allowed"
              >
                {updating ? '评分中...' : '评分列表'}
              </button>
              <button
                onClick={handleUpdateSelected}
                disabled={updating || loading || selectedProtocolIds.size === 0}
                className="px-6 py-2 bg-blue-600 text-white rounded-lg hover:bg-blue-700 disabled:opacity-50 disabled:cursor-not-allowed"
              >
                评分选择项 ({selectedProtocolIds.size})
              </button>
              <button
                onClick={() => {
                  resetCreateForm();
                  setIsCreateModalOpen(true);
                }}
                disabled={updating || loading}
                className="px-6 py-2 bg-blue-500 text-white rounded-lg hover:bg-blue-600 disabled:opacity-50 disabled:cursor-not-allowed"
              >
                ➕ 新建项目
              </button>
              <button
                onClick={() => handleClearScores('list')}
                disabled={updating || loading}
                className="px-6 py-2 bg-red-600 text-white rounded-lg hover:bg-red-700 disabled:opacity-50 disabled:cursor-not-allowed"
              >
                Clear Scores (Filtered)
              </button>
              <button
                onClick={() => handleClearScores('selected')}
                disabled={updating || loading || selectedProtocolIds.size === 0}
                className="px-6 py-2 bg-orange-600 text-white rounded-lg hover:bg-orange-700 disabled:opacity-50 disabled:cursor-not-allowed"
              >
                Clear Scores (Selected)
              </button>
              <button
                onClick={async () => {
                  if (!window.confirm('只增量添加新的协议（本地没有的 id），不修改已有协议，确定继续？')) {
                    return;
                  }

                  setUpdateLogs([{ time: new Date().toLocaleTimeString(), message: '开始增量同步新协议...' }]);
                  setActiveTask('defillama-add-new');
                  setUpdating(true);
                  setUpdateStatus('正在从 DeFiLlama 增量同步新协议...');
                  try {
                    const response = await authFetch(`${WORKER_API}/admin/api/add-new-from-defillama`, {
                      method: 'POST',
                      headers: {
                        'Content-Type': 'application/json',
                      },
                      body: JSON.stringify({})
                    });

                    if (!response.ok) {
                      const errorData = await response.json();
                      throw new Error(errorData.error || '增量同步失败');
                    }

                    const result = await response.json();
                    setUpdateStatus(result.message || '增量同步完成');
                    alert(result.message || '增量同步完成');
                    if (Array.isArray(result.logs)) {
                      const formatted = formatServerLogs(result.logs);
                      if (formatted.length) {
                        setUpdateLogs(formatted);
                      }
                    }

                    setTimeout(() => {
                      loadData(1, search, sortBy);
                    }, 1000);
                  } catch (err) {
                    setError('增量同步失败: ' + err.message);
                    setUpdateLogs(prev => [...prev, { time: new Date().toLocaleTimeString(), message: `增量同步错误：${err.message}` }]);
                  } finally {
                    setUpdating(false);
                    setActiveTask(null);
                    setTimeout(() => setUpdateStatus(''), 5000);
                  }
                }}
                disabled={updating || loading}
                className="px-6 py-2 bg-emerald-600 text-white rounded-lg hover:bg-emerald-700 disabled:opacity-50 disabled:cursor-not-allowed"
              >
                {updating && activeTask === 'defillama-add-new' ? '增量同步中...' : '➕ 增量添加新协议'}
              </button>
              <button
                onClick={async () => {
                  if (!window.confirm('只同步 TVL（从 DeFiLlama），不改其他字段，确定继续？')) {
                    return;
                  }

                  setUpdateLogs([{ time: new Date().toLocaleTimeString(), message: '开始同步 TVL...' }]);
                  setActiveTask('tvl');
                  setUpdating(true);
                  setUpdateStatus('正在从 DeFiLlama 同步 TVL...');
                  try {
                    const response = await authFetch(`${WORKER_API}/admin/api/update-tvl-from-defillama`, {
                      method: 'POST',
                      headers: {
                        'Content-Type': 'application/json',
                      },
                      body: JSON.stringify({})
                    });

                    if (!response.ok) {
                      const errorData = await response.json();
                      throw new Error(errorData.error || 'TVL 同步失败');
                    }

                    const result = await response.json();
                    setUpdateStatus(result.message || 'TVL 同步完成');
                    alert(result.message || 'TVL 同步完成');

                    setTimeout(() => {
                      loadData(1, search, sortBy);
                    }, 1000);
                  } catch (err) {
                    setError('TVL 同步失败: ' + err.message);
                    setUpdateLogs(prev => [...prev, { time: new Date().toLocaleTimeString(), message: `TVL 同步错误：${err.message}` }]);
                  } finally {
                    setUpdating(false);
                    setActiveTask(null);
                    setTimeout(() => setUpdateStatus(''), 5000);
                  }
                }}
                disabled={updating || loading}
                className="px-6 py-2 bg-sky-600 text-white rounded-lg hover:bg-sky-700 disabled:opacity-50 disabled:cursor-not-allowed"
              >
                {updating && activeTask === 'tvl' ? 'TVL 同步中...' : '📊 同步 TVL'}
              </button>
              <button
                onClick={async () => {
                  if (!window.confirm('仅同步 Logo 到 CDN，耗时较长，确定执行？')) {
                    return;
                  }

                  setUpdateLogs([{ time: new Date().toLocaleTimeString(), message: '开始同步 Logo...' }]);
                  setActiveTask('logos');
                  setUpdating(true);
                  setUpdateStatus('正在同步 Logo...');
                  try {
                    // 使用 token 认证，不再需要 password
                    const response = await fetch(`${WORKER_API}/admin/api/sync-logos`, {
                      method: 'POST',
                      headers: {
                        'Content-Type': 'application/json',
                      },
                      body: JSON.stringify({
                        // 使用 token 认证，不再需要 password
                      })
                    });

                    if (!response.ok) {
                      const errorData = await response.json();
                      throw new Error(errorData.error || 'Logo 同步失败');
                    }

                    const result = await response.json();
                    setUpdateStatus(result.message || 'Logo 同步完成');
                    alert(result.message || 'Logo 同步完成');
                    if (Array.isArray(result.logs)) {
                      const formatted = formatServerLogs(result.logs);
                      if (formatted.length) {
                        setUpdateLogs(formatted);
                      }
                    }

                    setTimeout(() => {
                      loadData(1, search, sortBy);
                    }, 1000);
                  } catch (err) {
                    setError('Logo 同步失败: ' + err.message);
                    setUpdateLogs(prev => [...prev, { time: new Date().toLocaleTimeString(), message: `错误：${err.message}` }]);
                  } finally {
                    setUpdating(false);
                    setActiveTask(null);
                    setTimeout(() => setUpdateStatus(''), 5000);
                  }
                }}
                disabled={updating || loading}
                className="px-6 py-2 bg-teal-600 text-white rounded-lg hover:bg-teal-700 disabled:opacity-50 disabled:cursor-not-allowed"
              >
                {updating && activeTask === 'logos' ? '同步中...' : '🖼️ 同步 Logo'}
              </button>
              <button
                onClick={() => setIsApyTokenModalOpen(true)}
                className="px-6 py-2 bg-slate-600 text-white rounded-lg hover:bg-slate-700"
                disabled={updating}
              >
                📊 查看 Token APY
              </button>
              <button
                onClick={async () => {
                  if (!window.confirm('仅刷新 APY 数据，确定执行？')) {
                    return;
                  }

                  setUpdateLogs([{ time: new Date().toLocaleTimeString(), message: '开始同步 APY 数据...' }]);
                  setActiveTask('apy');
                  setUpdating(true);
                  setUpdateStatus('正在同步 APY 数据...');
                  try {
                    // 使用带 Authorization header 的 authFetch，走 token 认证
                    const response = await authFetch(`${WORKER_API}/admin/api/sync-apy`, {
                      method: 'POST',
                      headers: {
                        'Content-Type': 'application/json',
                      },
                      body: JSON.stringify({})
                    });

                    if (!response.ok) {
                      const errorData = await response.json();
                      throw new Error(errorData.error || 'APY 同步失败');
                    }

                    const result = await response.json();
                    setUpdateStatus(result.message || 'APY 同步完成');
                    alert(result.message || 'APY 同步完成');
                    if (Array.isArray(result.logs)) {
                      const formatted = formatServerLogs(result.logs);
                      if (formatted.length) {
                        setUpdateLogs(formatted);
                      }
                    }

                    setTimeout(() => {
                      loadData(1, search, sortBy);
                    }, 1000);
                  } catch (err) {
                    setError('APY 同步失败: ' + err.message);
                    setUpdateLogs(prev => [...prev, { time: new Date().toLocaleTimeString(), message: `错误：${err.message}` }]);
                  } finally {
                    setUpdating(false);
                    setActiveTask(null);
                    setTimeout(() => setUpdateStatus(''), 5000);
                  }
                }}
                disabled={updating || loading}
                className="px-6 py-2 bg-emerald-600 text-white rounded-lg hover:bg-emerald-700 disabled:opacity-50 disabled:cursor-not-allowed"
              >
                {updating && activeTask === 'apy' ? '同步中...' : '📈 同步 APY'}
              </button>
              <button
                onClick={async () => {
                  if (!window.confirm('手动触发新闻抓取任务，将从 RSS 源抓取最新新闻并保存到数据库，确定执行？')) {
                    return;
                  }

                  setUpdateLogs([{ time: new Date().toLocaleTimeString(), message: '开始抓取新闻...' }]);
                  setActiveTask('news');
                  setUpdating(true);
                  setUpdateStatus('正在抓取新闻...');
                  try {
                    const response = await authFetch(`${WORKER_API}/admin/api/news/fetch`, {
                      method: 'POST',
                      headers: {
                        'Content-Type': 'application/json',
                      },
                      body: JSON.stringify({})
                    });

                    if (!response.ok) {
                      const errorData = await response.json();
                      throw new Error(errorData.error || '新闻抓取失败');
                    }

                    const result = await response.json();
                    const message = result.success 
                      ? `新闻抓取完成：保存 ${result.saved || 0} 条新新闻，跳过 ${result.skipped || 0} 条重复`
                      : `新闻抓取失败：${result.reason || result.error || '未知错误'}`;
                    
                    setUpdateStatus(message);
                    alert(message);
                    setUpdateLogs(prev => [...prev, { 
                      time: new Date().toLocaleTimeString(), 
                      message: message 
                    }]);
                  } catch (err) {
                    setError('新闻抓取失败: ' + err.message);
                    setUpdateLogs(prev => [...prev, { 
                      time: new Date().toLocaleTimeString(), 
                      message: `错误：${err.message}` 
                    }]);
                  } finally {
                    setUpdating(false);
                    setActiveTask(null);
                    setTimeout(() => setUpdateStatus(''), 5000);
                  }
                }}
                disabled={updating || loading}
                className="px-6 py-2 bg-purple-600 text-white rounded-lg hover:bg-purple-700 disabled:opacity-50 disabled:cursor-not-allowed"
              >
                {updating && activeTask === 'news' ? '抓取中...' : '📰 抓取新闻'}
              </button>
              <button
                onClick={() => {
                  loadConfig();
                  setIsConfigModalOpen(true);
                }}
                className="px-6 py-2 bg-purple-600 text-white rounded-lg hover:bg-purple-700"
              >
                ⚙️ Settings
              </button>
              <button
                onClick={() => setIsTwitterModalOpen(true)}
                className="px-6 py-2 bg-blue-500 text-white rounded-lg hover:bg-blue-600"
              >
                🐦 Twitter Agent
              </button>
            </div>
          </div>
        </div>

        {/* Filter Panel */}
        {showFilters && (
          <div className="bg-white rounded-xl border border-gray-200 p-6 mb-6 shadow-sm">
            <h2 className="text-lg font-semibold text-gray-900 mb-4">Update Filters</h2>
            <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-6">
              {/* TVL Range */}
              <div>
                <label className="block text-xs font-semibold text-gray-600 uppercase tracking-wide mb-2">
                  TVL Range (USD)
                </label>
                <div className="space-y-2">
                  <input
                    type="number"
                    value={minTvl}
                    onChange={(e) => setMinTvl(e.target.value)}
                    placeholder="Min TVL"
                    className="w-full px-3 py-2 border border-gray-300 rounded-lg text-sm"
                  />
                  <input
                    type="number"
                    value={maxTvl}
                    onChange={(e) => setMaxTvl(e.target.value)}
                    placeholder="Max TVL"
                    className="w-full px-3 py-2 border border-gray-300 rounded-lg text-sm"
                  />
                </div>
              </div>

              {/* Categories (Multi-select) */}
              <div>
                <label className="block text-xs font-semibold text-gray-600 uppercase tracking-wide mb-2">
                  Categories (Multi-select)
                </label>
                <div className="max-h-40 overflow-y-auto border border-gray-300 rounded-lg p-2">
                  {availableCategories.map((cat) => (
                    <label key={cat} className="flex items-center space-x-2 py-1">
                      <input
                        type="checkbox"
                        checked={selectedCategories.includes(cat)}
                        onChange={() => toggleCategory(cat)}
                        className="rounded border-gray-300"
                      />
                      <span className="text-sm text-gray-700">{cat}</span>
                    </label>
                  ))}
                </div>
              </div>

              {/* Blockchains (Multi-select) */}
              <div>
                <label className="block text-xs font-semibold text-gray-600 uppercase tracking-wide mb-2">
                  Blockchains (Multi-select)
                </label>
                <div className="max-h-40 overflow-y-auto border border-gray-300 rounded-lg p-2">
                  {availableBlockchains.map((chain) => (
                    <label key={chain} className="flex items-center space-x-2 py-1">
                      <input
                        type="checkbox"
                        checked={selectedBlockchains.includes(chain)}
                        onChange={() => toggleBlockchain(chain)}
                        className="rounded border-gray-300"
                      />
                      <span className="text-sm text-gray-700">{chain}</span>
                    </label>
                  ))}
                </div>
              </div>

              {/* Scores (Multi-select) */}
              <div>
                <label className="block text-xs font-semibold text-gray-600 uppercase tracking-wide mb-2">
                  Scores (Multi-select)
                </label>
                <div className="max-h-40 overflow-y-auto border border-gray-300 rounded-lg p-2">
                  {/* 没有分数选项 */}
                  <label className="flex items-center space-x-2 py-1">
                    <input
                      type="checkbox"
                      checked={selectedScores.includes('no-score')}
                      onChange={() => toggleScore('no-score')}
                      className="rounded border-gray-300"
                    />
                    <span className="text-sm text-gray-700">No Score</span>
                  </label>
                  {[0, 10, 20, 30, 40, 50, 60, 70, 80, 90, 100].map((score) => {
                    const range = score === 100 ? '100' : `${score}-${score + 9}`;
                    return (
                      <label key={score} className="flex items-center space-x-2 py-1">
                        <input
                          type="checkbox"
                          checked={selectedScores.includes(String(score))}
                          onChange={() => toggleScore(String(score))}
                          className="rounded border-gray-300"
                        />
                        <span className="text-sm text-gray-700">{range}</span>
                      </label>
                    );
                  })}
                </div>
              </div>
            </div>
            <div className="mt-4 flex gap-2">
              <button
                onClick={handleApplyFilters}
                className="px-4 py-2 bg-blue-600 text-white rounded-lg hover:bg-blue-700 text-sm"
              >
                Apply Filters
              </button>
              <button
                onClick={() => {
                  setMinTvl('');
                  setMaxTvl('');
                  setSelectedCategories([]);
                  setSelectedBlockchains([]);
                  setSelectedScores([]);
                  // 清除筛选后重新加载数据
                  setTimeout(() => {
                    loadData(1, search, sortBy, password, false);
                  }, 100);
                }}
                className="px-4 py-2 bg-gray-200 text-gray-700 rounded-lg hover:bg-gray-300 text-sm"
              >
                Clear All Filters
              </button>
            </div>
          </div>
        )}

        {/* Confirm Dialog */}
        {showConfirmDialog && (
          <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50">
            <div className="bg-white rounded-xl shadow-xl p-6 max-w-2xl w-full mx-4 max-h-[80vh] flex flex-col">
              <h2 className="text-xl font-bold text-gray-900 mb-4">Confirm Update</h2>
              <p className="text-gray-600 mb-4">
                The following {pendingUpdateList.length} protocol(s) will be updated:
              </p>
              <div className="flex-1 overflow-y-auto mb-4 border border-gray-200 rounded-lg p-4">
                <div className="space-y-2">
                  {pendingUpdateList.map((protocol, index) => (
                    <div key={protocol.id} className="flex items-center justify-between py-2 border-b border-gray-100">
                      <div className="flex-1">
                        <span className="font-medium text-gray-900">{index + 1}. {protocol.name}</span>
                        <div className="text-sm text-gray-500 mt-1">
                          TVL: ${(protocol.tvl / 1e6).toFixed(2)}M | 
                          Category: {protocol.category} | 
                          Chain: {protocol.blockchain}
                          {protocol.score !== undefined && ` | Current Score: ${protocol.score}`}
                        </div>
                      </div>
                    </div>
                  ))}
                </div>
              </div>
              <div className="flex gap-3 justify-end">
                <button
                  onClick={() => setShowConfirmDialog(false)}
                  className="px-4 py-2 bg-gray-200 text-gray-700 rounded-lg hover:bg-gray-300"
                >
                  Cancel
                </button>
                <button
                  onClick={handleConfirmUpdate}
                  className="px-4 py-2 bg-green-600 text-white rounded-lg hover:bg-green-700"
                >
                  Confirm and Update
                </button>
              </div>
            </div>
          </div>
        )}

        {isCreateModalOpen && (
          <div
            className="fixed inset-0 bg-black bg-opacity-40 flex items-center justify-center z-50"
            onClick={closeCreateModal}
          >
            <div
              className="bg-white rounded-2xl shadow-2xl p-6 w-full max-w-3xl mx-4 max-h-[90vh] overflow-y-auto"
              onClick={(e) => e.stopPropagation()}
            >
              <div className="flex items-center justify-between mb-4">
                <div>
                  <h3 className="text-xl font-semibold text-gray-900">新建项目</h3>
                  <p className="text-sm text-gray-500 mt-1">最少填写名称、公链，其他字段可后续补充。</p>
                </div>
                <button
                  onClick={closeCreateModal}
                  className="text-gray-400 hover:text-gray-600"
                >
                  ✕
                </button>
              </div>

              {createError && (
                <div className="mb-4 rounded-lg bg-red-50 border border-red-200 text-red-700 px-4 py-2 text-sm">
                  {createError}
                </div>
              )}

              <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                <div>
                  <label className="block text-xs font-semibold text-gray-600 uppercase tracking-wide mb-1">名称 *</label>
                  <input
                    type="text"
                    value={newProtocolData.name}
                    onChange={(e) => handleCreateFieldChange('name', e.target.value)}
                    className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-blue-500"
                    placeholder="项目名称"
                  />
                </div>
                <div>
                  <label className="block text-xs font-semibold text-gray-600 uppercase tracking-wide mb-1">公链 *</label>
                  <input
                    type="text"
                    value={newProtocolData.blockchain}
                    onChange={(e) => handleCreateFieldChange('blockchain', e.target.value)}
                    className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-blue-500"
                    placeholder="例如 Ethereum"
                  />
                </div>
                <div>
                  <label className="block text-xs font-semibold text-gray-600 uppercase tracking-wide mb-1">分类</label>
                  <input
                    type="text"
                    value={newProtocolData.category}
                    onChange={(e) => handleCreateFieldChange('category', e.target.value)}
                    className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-blue-500"
                    placeholder="例如 Lending"
                  />
                </div>
                <div>
                  <label className="block text-xs font-semibold text-gray-600 uppercase tracking-wide mb-1">TVL (USD)</label>
                  <input
                    type="number"
                    value={newProtocolData.tvl}
                    onChange={(e) => handleCreateFieldChange('tvl', e.target.value)}
                    className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-blue-500"
                    placeholder="0"
                  />
                </div>
                <div>
                  <label className="block text-xs font-semibold text-gray-600 uppercase tracking-wide mb-1">官网链接</label>
                  <div className="flex flex-col gap-2">
                    <div className="flex gap-2">
                      <input
                        type="url"
                        value={newProtocolData.url}
                        onChange={(e) => handleCreateFieldChange('url', e.target.value)}
                        className="flex-1 px-3 py-2 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-blue-500"
                        placeholder="https://"
                      />
                      <button
                        type="button"
                        onClick={handleAutoFillFromUrl}
                        disabled={autoFillLoading}
                        className="px-4 py-2 rounded-lg bg-emerald-600 text-white hover:bg-emerald-700 disabled:opacity-50"
                      >
                        {autoFillLoading ? 'AI 填写中...' : 'AI 自动填充'}
                      </button>
                    </div>
                    <p className="text-xs text-gray-500">
                      输入官网链接后，点击 AI 自动填充即可让 Agent 抓取基础信息。
                    </p>
                  </div>
                </div>
                <div>
                  <label className="block text-xs font-semibold text-gray-600 uppercase tracking-wide mb-1">Logo URL</label>
                  <input
                    type="url"
                    value={newProtocolData.logo}
                    onChange={(e) => handleCreateFieldChange('logo', e.target.value)}
                    className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-blue-500"
                    placeholder="https://cdn..."
                  />
                </div>
                <div>
                  <label className="block text-xs font-semibold text-gray-600 uppercase tracking-wide mb-1">自定义 Slug</label>
                  <input
                    type="text"
                    value={newProtocolData.slug}
                    onChange={(e) => handleCreateFieldChange('slug', e.target.value)}
                    className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-blue-500"
                    placeholder="可选，不填自动生成"
                  />
                </div>
                <div>
                  <label className="block text-xs font-semibold text-gray-600 uppercase tracking-wide mb-1">状态</label>
                  <select
                    value={newProtocolData.hidden ? 'hidden' : 'published'}
                    onChange={(e) => handleCreateFieldChange('hidden', e.target.value === 'hidden')}
                    className="w-full px-3 py-2 border border-gray-300 rounded-lg bg-white focus:outline-none focus:ring-2 focus:ring-blue-500"
                  >
                    <option value="published">发布（前台可见）</option>
                    <option value="hidden">隐藏（仅后台）</option>
                  </select>
                  <p className="text-xs text-gray-500 mt-1">隐藏状态下，项目不会出现在首页或搜索结果里。</p>
                </div>
                <div className="md:col-span-2">
                  <label className="block text-xs font-semibold text-gray-600 uppercase tracking-wide mb-1">简介</label>
                  <textarea
                    value={newProtocolData.description}
                    onChange={(e) => handleCreateFieldChange('description', e.target.value)}
                    className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-blue-500"
                    rows="4"
                    placeholder="可选，简要描述项目"
                  />
                </div>
              </div>

              <div className="flex justify-end gap-3 mt-6">
                <button
                  onClick={closeCreateModal}
                  className="px-4 py-2 rounded-lg border border-gray-300 text-gray-700 hover:bg-gray-100"
                  disabled={creatingProtocol}
                >
                  取消
                </button>
                <button
                  onClick={handleCreateProtocol}
                  className="px-6 py-2 rounded-lg bg-blue-600 text-white hover:bg-blue-700 disabled:opacity-50"
                  disabled={creatingProtocol}
                >
                  {creatingProtocol ? '创建中...' : '创建'}
                </button>
              </div>
            </div>
          </div>
        )}

        {/* Update Status and Logs */}
        {updating && (
          <div className="bg-white rounded-xl border border-gray-200 p-6 mb-6 shadow-sm">
            <h3 className="text-lg font-semibold text-gray-900 mb-4">Update Progress</h3>
            {updateStatus && (
              <div className="bg-blue-50 border border-blue-200 text-blue-700 px-4 py-3 rounded-lg mb-4">
                {updateStatus}
              </div>
            )}
            {updateLogs.length > 0 && (
              <div className="bg-gray-50 border border-gray-200 rounded-lg p-4 max-h-96 overflow-y-auto">
                <div className="space-y-1 font-mono text-sm">
                  {updateLogs.map((log, index) => (
                    <div key={index} className="text-gray-700">
                      <span className="text-gray-500">[{log.time}]</span> {log.message}
                    </div>
                  ))}
                </div>
              </div>
            )}
          </div>
        )}

        {updateStatus && !updating && (
          <div className="bg-green-50 border border-green-200 text-green-700 px-4 py-3 rounded-lg mb-4">
            {updateStatus}
          </div>
        )}

        {error && (
          <div className="bg-red-50 border border-red-200 text-red-700 px-4 py-3 rounded-lg mb-4">
            {error}
          </div>
        )}

        {/* Table */}
        <div className="bg-white rounded-xl border border-gray-200 shadow-sm overflow-hidden">
          <div className="overflow-x-auto">
            <table className="min-w-full divide-y divide-gray-200">
              <thead className="bg-gray-50">
                <tr>
                  <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                    <input
                      type="checkbox"
                      checked={selectedProtocolIds.size === protocols.length && protocols.length > 0}
                      onChange={toggleSelectAll}
                      className="rounded border-gray-300"
                    />
                  </th>
                  <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                    ID
                  </th>
                  <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                    Name
                  </th>
                  <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                    Status
                  </th>
                  <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                    Category
                  </th>
                  <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                    Blockchain
                  </th>
                  <th className="px-6 py-3 text-right text-xs font-medium text-gray-500 uppercase tracking-wider">
                    TVL
                  </th>
                  <th className="px-6 py-3 text-right text-xs font-medium text-gray-500 uppercase tracking-wider">
                    APY
                  </th>
                  <th className="px-6 py-3 text-right text-xs font-medium text-gray-500 uppercase tracking-wider">
                    Score
                  </th>
                  <th className="px-6 py-3 text-center text-xs font-medium text-gray-500 uppercase tracking-wider">
                    Reports
                  </th>
                  <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                    Last Updated
                  </th>
                  <th className="px-6 py-3 text-center text-xs font-medium text-gray-500 uppercase tracking-wider">
                    Actions
                  </th>
                </tr>
              </thead>
              <tbody className="bg-white divide-y divide-gray-200">
                {loading ? (
                  <tr>
                    <td colSpan="12" className="px-6 py-8 text-center text-gray-500">
                      Loading...
                    </td>
                  </tr>
                ) : protocols.length === 0 ? (
                  <tr>
                    <td colSpan="12" className="px-6 py-8 text-center text-gray-500">
                      No protocols found
                    </td>
                  </tr>
                ) : (
                  protocols.map((protocol) => {
                    const apyData = getProtocolApyData(protocol);
                    return (
                    <tr key={protocol.id} className="hover:bg-gray-50">
                      <td className="px-6 py-4 whitespace-nowrap">
                        <input
                          type="checkbox"
                          checked={selectedProtocolIds.has(protocol.id)}
                          onChange={() => toggleProtocolSelection(protocol.id)}
                          className="rounded border-gray-300"
                        />
                      </td>
                      <td className="px-6 py-4 whitespace-nowrap text-sm font-medium text-gray-900">
                        {protocol.id}
                      </td>
                      <td className="px-6 py-4 whitespace-nowrap">
                        <div className="flex items-center">
                          {protocol.logo && (
                            <img
                              src={protocol.logo}
                              alt={protocol.name}
                              className="w-8 h-8 rounded-full mr-3"
                              onError={(e) => {
                                e.target.onerror = null;
                                e.target.style.display = 'none';
                              }}
                            />
                          )}
                          <span className="text-sm font-medium text-gray-900">{protocol.name}</span>
                        </div>
                      </td>
                      <td className="px-6 py-4 whitespace-nowrap text-sm">
                        <div className="flex flex-col gap-1">
                          <span className={`inline-flex items-center px-2.5 py-0.5 rounded-full text-xs font-semibold ${protocol.hidden ? 'bg-gray-200 text-gray-600' : 'bg-green-100 text-green-800'}`}>
                            {protocol.hidden ? '隐藏' : '已发布'}
                          </span>
                          <button
                            onClick={() => handleToggleVisibility(protocol)}
                            disabled={visibilityUpdatingId === protocol.id}
                            className="text-xs text-blue-600 hover:text-blue-800 disabled:opacity-50 disabled:cursor-not-allowed"
                          >
                            {visibilityUpdatingId === protocol.id ? '处理中...' : protocol.hidden ? '发布' : '隐藏'}
                          </button>
                        </div>
                      </td>
                      <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-500">
                        {protocol.category || '—'}
                      </td>
                      <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-500">
                        {protocol.blockchain || '—'}
                      </td>
                      <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-900 text-right font-medium">
                        {formatTvl(protocol.tvl)}
                      </td>
                      <td className="px-6 py-4 whitespace-nowrap text-right text-sm text-gray-900">
                        <div className="font-semibold">{apyData.total}</div>
                        {(apyData.base !== '—' || apyData.reward !== '—') && (
                          <div className="text-xs text-gray-500">
                            基础 {apyData.base} · 奖励 {apyData.reward}
                          </div>
                        )}
                        {apyData.updated && (
                          <div className="text-[11px] text-gray-400">
                            {apyData.updated}
                          </div>
                        )}
                      </td>
                      <td className="px-6 py-4 whitespace-nowrap text-right">
                        {typeof protocol.score === 'number' ? (
                          <div className="flex flex-col items-end gap-1">
                          <div className="flex items-center justify-end gap-2">
                            <span
                              role="button"
                              tabIndex={0}
                              onClick={() => handleScoreClick(protocol)}
                              onKeyDown={(e) => {
                                if (e.key === 'Enter') handleScoreClick(protocol);
                              }}
                              className={`inline-flex items-center px-3 py-1.5 rounded-lg text-sm font-bold cursor-pointer hover:shadow-md transition-shadow ${getScoreColor(protocol.score)}`}
                              title="Click to view report"
                            >
                              {Math.round(protocol.score)}
                              <span className="ml-2 text-xs font-semibold opacity-80">
                                {calculateGrade(protocol.score)}
                              </span>
                            </span>
                            <button
                              onClick={() => handleHistoryClick(protocol)}
                              className="text-xs text-gray-400 hover:text-gray-600 px-2 py-1 rounded hover:bg-gray-100"
                              title="View update history"
                            >
                              📊
                            </button>
                            </div>
                            {protocol.lastScoreUpdatedAt && (
                              <div className="text-xs text-gray-500">
                                {formatTimeAgo(protocol.lastScoreUpdatedAt)}
                              </div>
                            )}
                          </div>
                        ) : (
                          <span className="text-sm text-gray-400">—</span>
                        )}
                      </td>
                      <td className="px-6 py-4 whitespace-nowrap text-center">
                        <div className="flex flex-col items-center gap-1.5">
                          <span className="text-sm font-semibold text-gray-900">
                            {protocol.reportCount || 0} {protocol.reportCount === 1 ? 'report' : 'reports'}
                          </span>
                          {protocol.recentScores && protocol.recentScores.length > 0 && (
                            <div className="flex items-center gap-1 flex-wrap justify-center">
                              {protocol.recentScores.map((score, idx) => (
                                <span
                                  key={idx}
                                  className={`text-xs font-bold px-2 py-0.5 rounded ${getScoreColor(score)}`}
                                  title={idx === 0 ? `Latest score: ${score}` : `Report ${idx + 1} score: ${score}`}
                                >
                                  {score}
                                </span>
                              ))}
                            </div>
                          )}
                        </div>
                      </td>
                      <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-500">
                        {formatTimeAgo(protocol.lastScoreUpdatedAt)}
                      </td>
                      <td className="px-6 py-4 whitespace-nowrap text-center">
                        <div className="flex items-center justify-center gap-2">
                          <button
                            onClick={() => handleGenerateFaviconLogo(protocol)}
                            disabled={faviconUpdatingId === protocol.id || !protocol.url}
                            className="text-sm text-emerald-600 hover:text-emerald-800 px-3 py-1.5 rounded hover:bg-emerald-50 disabled:opacity-50 disabled:cursor-not-allowed"
                          >
                            {faviconUpdatingId === protocol.id ? '抓取中...' : 'Favicon'}
                          </button>
                          <button
                            onClick={() => handleNewsClick(protocol)}
                            className="text-sm text-purple-600 hover:text-purple-800 px-3 py-1.5 rounded hover:bg-purple-50 transition-colors"
                            title="View news"
                          >
                            新闻
                          </button>
                          <button
                            onClick={() => {
                              setEditingProtocol(protocol);
                              setEditFormData({
                                name: protocol.name || '',
                                category: protocol.category || '',
                                blockchain: protocol.blockchain || '',
                                tvl: protocol.tvl ? String(protocol.tvl) : '',
                                url: protocol.url || '',
                                app_url: protocol.app_url || '',
                                description: protocol.description || '',
                                logo: protocol.logo || '',
                                slug: protocol.slug || ''
                              });
                              setIsEditModalOpen(true);
                            }}
                            className="text-blue-600 hover:text-blue-800 text-sm font-medium px-3 py-1.5 rounded hover:bg-blue-50 transition-colors"
                            title="Edit protocol"
                          >
                            Edit
                          </button>
                        </div>
                      </td>
                    </tr>
                  )})
                )}
              </tbody>
            </table>
          </div>
        </div>

        {/* Pagination */}
        {pagination && pagination.totalPages > 1 && (
          <div className="mt-6 flex items-center justify-between">
            <div className="text-sm text-gray-700">
              Showing {((currentPage - 1) * 100) + 1} to {Math.min(currentPage * 100, pagination.totalItems)} of {pagination.totalItems} protocols
            </div>
            <div className="flex gap-2">
              <button
                onClick={() => handlePageChange(currentPage - 1)}
                disabled={currentPage === 1 || loading}
                className="px-4 py-2 border border-gray-300 rounded-lg hover:bg-gray-50 disabled:opacity-50 disabled:cursor-not-allowed"
              >
                Previous
              </button>
              <span className="px-4 py-2 text-sm text-gray-700">
                Page {currentPage} of {pagination.totalPages}
              </span>
              <button
                onClick={() => handlePageChange(currentPage + 1)}
                disabled={currentPage === pagination.totalPages || loading}
                className="px-4 py-2 border border-gray-300 rounded-lg hover:bg-gray-50 disabled:opacity-50 disabled:cursor-not-allowed"
              >
                Next
              </button>
            </div>
          </div>
        )}

        {/* Report Modal - 显示历史报告列表 */}
        {isReportModalOpen && (
          <div 
            className="fixed inset-0 z-50 flex items-center justify-center bg-black/40 px-4"
            onClick={closeReportModal}
          >
            <div 
              className="w-full max-w-4xl rounded-lg bg-white p-6 shadow-2xl max-h-[90vh] overflow-y-auto"
              onClick={(e) => e.stopPropagation()}
            >
              <div className="flex items-start justify-between">
                <div>
                  <p className="text-sm uppercase tracking-wide text-gray-400">Credit Score History</p>
                  <h3 className="text-2xl font-semibold text-gray-900">
                    {selectedProject?.name || 'Untitled protocol'}
                  </h3>
                  <p className="text-sm text-gray-500">
                    {selectedProject?.category}
                  </p>
                  {historyData.length > 0 && (
                    <p className="text-sm font-medium text-blue-600 mt-1">
                      {historyData.length} {historyData.length === 1 ? 'Report' : 'Reports'} Available
                    </p>
                  )}
                </div>
                <button
                  type="button"
                  onClick={closeReportModal}
                  className="text-gray-400 hover:text-gray-600"
                  aria-label="Close report"
                >
                  ✕
                </button>
              </div>

              <div className="mt-6">
                {reportLoading && (
                  <div className="text-center text-gray-500">Loading reports...</div>
                )}

                {reportError && (
                  <div className="rounded-md bg-red-50 p-4 text-sm text-red-600">
                    {reportError}
                  </div>
                )}

                {!reportLoading && !reportError && historyData.length > 0 && (
                  <div className="space-y-4">
                    {historyData.map((report, index) => {
                      const score = report?.overall_risk?.risk_score ?? report.score ?? selectedProject?.score;
                      const grade = report?.overall_risk?.grade ?? calculateGrade(score);
                      const isCurrent = report.isCurrent;
                      
                      return (
                        <div key={index} className={`border rounded-lg p-4 ${isCurrent ? 'border-blue-300 bg-blue-50' : 'border-gray-200 bg-white'}`}>
                          <div className="flex items-center justify-between mb-3">
                            <div className="flex items-center gap-3">
                              {isCurrent && (
                                <span className="inline-flex items-center px-2 py-1 rounded text-xs font-semibold bg-blue-100 text-blue-800">
                                  Current
                                </span>
                              )}
                              <span className={`inline-flex items-center px-3 py-1 rounded-lg text-sm font-bold ${getScoreColor(score)}`}>
                                {score}
                                <span className="ml-2 text-xs font-semibold opacity-80">
                                  {grade}
                                </span>
                              </span>
                              <span className="text-sm text-gray-500">
                                {formatTimeAgo(report.timestamp)}
                              </span>
                            </div>
                            <div className="flex items-center gap-2">
                              {/* 所有报告都可以删除 */}
                              <button
                                onClick={() => handleDeleteHistory(report.historyKey, isCurrent)}
                                className="text-red-500 hover:text-red-700 text-sm px-2 py-1 rounded hover:bg-red-50"
                                title={isCurrent ? "Clear current score" : "Delete this history report"}
                              >
                                🗑️ Delete
                              </button>
                            </div>
                          </div>
                          
                          {report?.overall_risk?.risk_summary_text && (
                            <p className="text-sm text-gray-600 mb-3">{report.overall_risk.risk_summary_text}</p>
                          )}
                          
                          {/* 显示完整报告内容 */}
                          <details className="mt-3">
                            <summary className="cursor-pointer text-sm font-semibold text-gray-700 hover:text-gray-900">
                              View Full Report
                            </summary>
                            <div className="mt-3 pt-3 border-t border-gray-200 space-y-4">
                              {report?.top_risk_drivers?.length > 0 && (
                                <div>
                                  <h5 className="text-sm font-semibold text-gray-900 mb-2">Top Risk Drivers</h5>
                                  <ul className="list-disc space-y-1 pl-5 text-sm text-gray-600">
                                    {report.top_risk_drivers.map((risk, idx) => (
                                      <li key={idx}>{risk}</li>
                                    ))}
                                  </ul>
                                </div>
                              )}
                              
                              {report?.investment_suggestion?.actionable_advice?.length > 0 && (
                                <div>
                                  <h5 className="text-sm font-semibold text-gray-900 mb-2">Actionable Advice</h5>
                                  <ul className="list-disc space-y-1 pl-5 text-sm text-gray-600">
                                    {report.investment_suggestion.actionable_advice.map((advice, idx) => (
                                      <li key={idx}>{advice}</li>
                                    ))}
                                  </ul>
                                </div>
                              )}
                              
                              {report?.subscores && (
                                <div>
                                  <h5 className="text-sm font-semibold text-gray-900 mb-2">Subscores</h5>
                                  <div className="grid grid-cols-2 gap-2 text-sm">
                                    {Object.entries(report.subscores).map(([key, subscore]) => (
                                      <div key={key} className="flex justify-between">
                                        <span className="text-gray-600 capitalize">{key}:</span>
                                        <span className="font-medium">{subscore.score}</span>
                                      </div>
                                    ))}
                                  </div>
                                </div>
                              )}
                              
                              {report?.disclaimer?.length > 0 && (
                                <div>
                                  <h5 className="text-sm font-semibold text-gray-900 mb-2">Disclaimers</h5>
                                  <div className="rounded-md bg-gray-50 p-3 text-xs text-gray-500">
                                    {report.disclaimer.map((item, idx) => (
                                      <p key={idx} className="mt-1">{item}</p>
                                    ))}
                                  </div>
                                </div>
                              )}
                            </div>
                          </details>
                          
                          <p className="text-xs text-gray-400 mt-2">
                            {new Date(report.timestamp).toLocaleString()}
                          </p>
                        </div>
                      );
                    })}
                  </div>
                )}
                
                {!reportLoading && !reportError && historyData.length === 0 && (
                  <div className="text-center text-gray-500 py-8">
                    No history reports available
                  </div>
                )}
              </div>
            </div>
          </div>
        )}

        {/* History Modal */}
        {isHistoryModalOpen && (
          <div 
            className="fixed inset-0 z-50 flex items-center justify-center bg-black/40 px-4"
            onClick={closeHistoryModal}
          >
            <div 
              className="w-full max-w-2xl rounded-lg bg-white p-6 shadow-2xl max-h-[90vh] overflow-y-auto"
              onClick={(e) => e.stopPropagation()}
            >
              <div className="flex items-start justify-between">
                <div>
                  <p className="text-sm uppercase tracking-wide text-gray-400">Update History</p>
                  <h3 className="text-2xl font-semibold text-gray-900">
                    {historyProtocol?.name || 'Untitled protocol'}
                  </h3>
                </div>
                <button
                  type="button"
                  onClick={closeHistoryModal}
                  className="text-gray-400 hover:text-gray-600"
                  aria-label="Close history"
                >
                  ✕
                </button>
              </div>

              <div className="mt-6">
                {historyLoading && (
                  <div className="text-center text-gray-500">Loading history...</div>
                )}

                {!historyLoading && historyData.length === 0 && (
                  <div className="text-center text-gray-500">No update history available</div>
                )}

                {!historyLoading && historyData.length > 0 && (
                  <div className="space-y-4">
                    {historyData.map((item, index) => (
                      <div key={index} className="border border-gray-200 rounded-lg p-4">
                        <div className="flex items-center justify-between mb-3">
                          <div className="flex items-center gap-3">
                            <span className={`inline-flex items-center px-3 py-1 rounded-lg text-sm font-bold ${getScoreColor(item?.overall_risk?.risk_score ?? item.score)}`}>
                              {item?.overall_risk?.risk_score ?? item.score}
                              <span className="ml-2 text-xs font-semibold opacity-80">
                                {item?.overall_risk?.grade ?? item.grade ?? calculateGrade(item?.overall_risk?.risk_score ?? item.score)}
                              </span>
                            </span>
                          </div>
                          <div className="flex items-center gap-2">
                            <span className="text-sm text-gray-500">
                              {formatTimeAgo(item.timestamp)}
                            </span>
                            <button
                              onClick={() => handleDeleteHistory(item.historyKey)}
                              className="text-red-500 hover:text-red-700 text-sm px-2 py-1 rounded hover:bg-red-50"
                              title="Delete this history report"
                            >
                              🗑️
                            </button>
                          </div>
                        </div>
                        {item?.overall_risk?.risk_summary_text && (
                          <p className="text-sm text-gray-600 mt-2">{item.overall_risk.risk_summary_text}</p>
                        )}
                        <p className="text-xs text-gray-400 mt-1">
                          {new Date(item.timestamp).toLocaleString()}
                        </p>
                        
                        {/* 显示完整报告内容（和当前报告一样） */}
                        <div className="mt-4 pt-4 border-t border-gray-200">
                          {item?.top_risk_drivers?.length > 0 && (
                            <div className="mb-3">
                              <h5 className="text-sm font-semibold text-gray-900 mb-1">Top Risk Drivers</h5>
                              <ul className="list-disc space-y-1 pl-5 text-xs text-gray-600">
                                {item.top_risk_drivers.map((risk, idx) => (
                                  <li key={idx}>{risk}</li>
                                ))}
                              </ul>
                            </div>
                          )}
                          
                          {item?.investment_suggestion?.actionable_advice?.length > 0 && (
                            <div className="mb-3">
                              <h5 className="text-sm font-semibold text-gray-900 mb-1">Actionable Advice</h5>
                              <ul className="list-disc space-y-1 pl-5 text-xs text-gray-600">
                                {item.investment_suggestion.actionable_advice.map((advice, idx) => (
                                  <li key={idx}>{advice}</li>
                                ))}
                              </ul>
                            </div>
                          )}
                          
                          {item?.subscores && (
                            <div className="mb-3">
                              <h5 className="text-sm font-semibold text-gray-900 mb-1">Subscores</h5>
                              <div className="grid grid-cols-2 gap-2 text-xs">
                                {Object.entries(item.subscores).map(([key, subscore]) => (
                                  <div key={key} className="flex justify-between">
                                    <span className="text-gray-600 capitalize">{key}:</span>
                                    <span className="font-medium">{subscore.score}</span>
                                  </div>
                                ))}
                              </div>
                            </div>
                          )}
                        </div>
                      </div>
                    ))}
                  </div>
                )}
              </div>
            </div>
          </div>
        )}

        {/* Edit Modal */}
        {isEditModalOpen && editingProtocol && (
          <div 
            className="fixed inset-0 z-50 flex items-center justify-center bg-black/40 px-4"
            onClick={() => setIsEditModalOpen(false)}
          >
            <div 
              className="w-full max-w-2xl rounded-lg bg-white shadow-2xl max-h-[90vh] flex flex-col"
              onClick={(e) => e.stopPropagation()}
            >
              <div className="flex items-start justify-between p-6 border-b border-gray-200 flex-shrink-0">
                <div>
                  <h3 className="text-2xl font-semibold text-gray-900">Edit Protocol</h3>
                  <p className="text-sm text-gray-500 mt-1">{editingProtocol.name}</p>
                </div>
                <button
                  type="button"
                  onClick={() => setIsEditModalOpen(false)}
                  className="text-gray-400 hover:text-gray-600 text-2xl"
                  aria-label="Close"
                >
                  ✕
                </button>
              </div>

              <div className="p-6 overflow-y-auto flex-1">
                <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-2">
                      Name *
                    </label>
                    <input
                      type="text"
                      value={editFormData.name}
                      onChange={(e) => setEditFormData({ ...editFormData, name: e.target.value })}
                      placeholder="Protocol name"
                      className="w-full px-4 py-2 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-blue-500"
                      required
                    />
                  </div>
                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-2">
                      Category
                    </label>
                    <input
                      type="text"
                      value={editFormData.category}
                      onChange={(e) => setEditFormData({ ...editFormData, category: e.target.value })}
                      placeholder="Category"
                      className="w-full px-4 py-2 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-blue-500"
                    />
                  </div>
                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-2">
                      Blockchain
                    </label>
                    <input
                      type="text"
                      value={editFormData.blockchain}
                      onChange={(e) => setEditFormData({ ...editFormData, blockchain: e.target.value })}
                      placeholder="Blockchain"
                      className="w-full px-4 py-2 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-blue-500"
                    />
                  </div>
                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-2">
                      TVL (USD)
                    </label>
                    <input
                      type="number"
                      value={editFormData.tvl}
                      onChange={(e) => setEditFormData({ ...editFormData, tvl: e.target.value })}
                      placeholder="0"
                      className="w-full px-4 py-2 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-blue-500"
                    />
                  </div>
                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-2">
                      Homepage URL
                    </label>
                    <input
                      type="url"
                      value={editFormData.url}
                      onChange={(e) => setEditFormData({ ...editFormData, url: e.target.value })}
                      placeholder="https://example.com"
                      className="w-full px-4 py-2 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-blue-500"
                    />
                  </div>
                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-2">
                      DApp URL
                    </label>
                    <input
                      type="url"
                      value={editFormData.app_url}
                      onChange={(e) => setEditFormData({ ...editFormData, app_url: e.target.value })}
                      placeholder="https://app.example.com"
                      className="w-full px-4 py-2 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-blue-500"
                    />
                  </div>
                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-2">
                      Logo URL
                    </label>
                    <div className="flex gap-2">
                      <input
                        type="url"
                        value={editFormData.logo}
                        onChange={(e) => setEditFormData({ ...editFormData, logo: e.target.value })}
                        placeholder="https://example.com/logo.png"
                        className="flex-1 px-4 py-2 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-blue-500"
                      />
                      <input
                        type="file"
                        accept="image/*"
                        onChange={async (e) => {
                          const file = e.target.files?.[0];
                          if (file) {
                            // 检查文件大小（限制为 2MB）
                            if (file.size > 2 * 1024 * 1024) {
                              alert('Image size must be less than 2MB');
                              e.target.value = '';
                              return;
                            }
                            
                            const reader = new FileReader();
                            reader.onload = async (event) => {
                              const base64Data = event.target.result;
                              setEditFormData({ ...editFormData, logo: base64Data });
                              setLogoSaving(true);
                              
                              // 自动保存到后端
                              try {
                                // 使用 token 认证，不再需要 password
                                const response = await authFetch(`${WORKER_API}/admin/api/update-protocol`, {
                                  method: 'POST',
                                  headers: {
                                    'Content-Type': 'application/json',
                                  },
                                  body: JSON.stringify({
                                    // 使用 token 认证，不再需要 password,
                                    id: editingProtocol.id,
                                    logo: base64Data
                                  })
                                });

                                if (!response.ok) {
                                  const errorData = await response.json().catch(() => ({}));
                                  throw new Error(errorData.error || 'Failed to save logo');
                                }

                                // 更新本地协议数据
                                setProtocols(prev => prev.map(p => 
                                  p.id === editingProtocol.id 
                                    ? { ...p, logo: base64Data }
                                    : p
                                ));
                                
                                // 更新 editingProtocol
                                setEditingProtocol({ ...editingProtocol, logo: base64Data });
                              } catch (err) {
                                console.error('Failed to auto-save logo:', err);
                                alert('Failed to auto-save logo: ' + err.message);
                              } finally {
                                setLogoSaving(false);
                              }
                            };
                            reader.readAsDataURL(file);
                          }
                          e.target.value = ''; // Reset input
                        }}
                        className="hidden"
                        id={`logo-upload-${editingProtocol?.id}`}
                      />
                      <label
                        htmlFor={`logo-upload-${editingProtocol?.id}`}
                        className="px-3 py-2 text-sm bg-blue-100 text-blue-700 rounded-lg hover:bg-blue-200 transition-colors whitespace-nowrap cursor-pointer"
                        title="Upload logo from local"
                      >
                        📁
                      </label>
                      {editFormData.logo && !editFormData.logo.startsWith('data:') && (
                        <button
                          type="button"
                          onClick={async () => {
                            try {
                              const response = await fetch(editFormData.logo, { mode: 'cors' });
                              const blob = await response.blob();
                              const url = window.URL.createObjectURL(blob);
                              const a = document.createElement('a');
                              a.href = url;
                              a.download = `${editingProtocol.name || 'logo'}-${editingProtocol.id}.${blob.type.split('/')[1] || 'png'}`;
                              document.body.appendChild(a);
                              a.click();
                              document.body.removeChild(a);
                              window.URL.revokeObjectURL(url);
                            } catch (err) {
                              alert('Failed to download logo: ' + err.message);
                            }
                          }}
                          className="px-3 py-2 text-sm bg-gray-100 text-gray-700 rounded-lg hover:bg-gray-200 transition-colors whitespace-nowrap"
                          title="Download logo"
                        >
                          ⬇️
                        </button>
                      )}
                    </div>
                    {editFormData.logo && (
                      <div className="mt-2">
                        <img
                          src={editFormData.logo}
                          alt="Logo preview"
                          className="w-16 h-16 rounded-lg border border-gray-200 object-cover bg-gray-50"
                          onError={(e) => {
                            e.target.style.display = 'none';
                          }}
                        />
                        {editFormData.logo.startsWith('data:') && (
                          <p className="text-xs mt-1">
                            {logoSaving ? (
                              <span className="text-blue-600">⏳ Saving logo...</span>
                            ) : (
                              <span className="text-green-600">✓ Logo automatically saved</span>
                            )}
                          </p>
                        )}
                      </div>
                    )}
                  </div>
                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-2">
                      Slug
                    </label>
                    <input
                      type="text"
                      value={editFormData.slug}
                      onChange={(e) => setEditFormData({ ...editFormData, slug: e.target.value })}
                      placeholder="protocol-slug"
                      className="w-full px-4 py-2 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-blue-500"
                    />
                  </div>
                  <div className="md:col-span-2">
                    <label className="block text-sm font-medium text-gray-700 mb-2">
                      Description
                    </label>
                    <textarea
                      value={editFormData.description}
                      onChange={(e) => setEditFormData({ ...editFormData, description: e.target.value })}
                      placeholder="Protocol description"
                      rows={4}
                      className="w-full px-4 py-2 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-blue-500 resize-none"
                    />
                  </div>
                </div>
              </div>

              <div className="flex items-center justify-end gap-3 p-6 border-t border-gray-200 flex-shrink-0">
                <button
                  type="button"
                  onClick={() => setIsEditModalOpen(false)}
                  className="px-4 py-2 text-gray-700 bg-gray-100 rounded-lg hover:bg-gray-200 transition-colors"
                >
                  Cancel
                </button>
                <button
                  type="button"
                  onClick={async () => {
                    if (!editFormData.name || editFormData.name.trim() === '') {
                      alert('Name is required');
                      return;
                    }
                    
                    setEditLoading(true);
                    try {
                      // 使用 token 认证，不再需要 password
                      const response = await authFetch(`${WORKER_API}/admin/api/update-protocol`, {
                        method: 'POST',
                        headers: {
                          'Content-Type': 'application/json',
                        },
                        body: JSON.stringify({
                          // 使用 token 认证，不再需要 password,
                          id: editingProtocol.id,
                          name: editFormData.name.trim(),
                          category: editFormData.category.trim() || undefined,
                          blockchain: editFormData.blockchain.trim() || undefined,
                          tvl: editFormData.tvl ? parseFloat(editFormData.tvl) : undefined,
                          url: editFormData.url.trim() || '#',
                          app_url: editFormData.app_url.trim() || undefined,
                          description: editFormData.description.trim() || undefined,
                          logo: editFormData.logo.trim() || undefined,
                          slug: editFormData.slug.trim() || undefined
                        })
                      });

                      if (!response.ok) {
                        const errorData = await response.json().catch(() => ({}));
                        throw new Error(errorData.error || 'Failed to update protocol');
                      }

                      const result = await response.json();
                      alert('Protocol updated successfully!');
                      setIsEditModalOpen(false);
                      // Reload data
                      // 使用 token 认证，不再需要 password
                      loadData(currentPage, search, sortBy, true);
                    } catch (err) {
                      setError('Failed to update protocol: ' + err.message);
                    } finally {
                      setEditLoading(false);
                    }
                  }}
                  disabled={editLoading}
                  className="px-4 py-2 bg-blue-600 text-white rounded-lg hover:bg-blue-700 transition-colors disabled:opacity-50 disabled:cursor-not-allowed"
                >
                  {editLoading ? 'Saving...' : 'Save Changes'}
                </button>
              </div>
            </div>
          </div>
        )}

        {/* Config Modal */}
        {isConfigModalOpen && (
          <div 
            className="fixed inset-0 z-50 flex items-center justify-center bg-black/40 px-4"
            onClick={closeConfigModal}
          >
            <div 
              className="w-full max-w-2xl rounded-lg bg-white shadow-2xl max-h-[90vh] flex flex-col"
              onClick={(e) => e.stopPropagation()}
            >
              <div className="flex items-start justify-between p-6 border-b border-gray-200 flex-shrink-0">
                <div>
                  <h3 className="text-2xl font-semibold text-gray-900">System Settings</h3>
                  <p className="text-sm text-gray-500 mt-1">Configure notification email and update interval</p>
                </div>
                <button
                  type="button"
                  onClick={closeConfigModal}
                  className="text-gray-400 hover:text-gray-600 text-2xl"
                  aria-label="Close"
                >
                  ✕
                </button>
              </div>

              <div className="p-6 overflow-y-auto flex-1">
                <div className="space-y-6">
                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-2">
                      Notification Email *
                    </label>
                    <input
                      type="email"
                      value={configData.notificationEmail}
                      onChange={(e) => setConfigData({ ...configData, notificationEmail: e.target.value })}
                      placeholder="your-email@example.com"
                      className="w-full px-4 py-2 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-blue-500"
                    />
                    <p className="text-xs text-gray-500 mt-1">
                      Email address to receive notifications when data fetch fails
                    </p>
                  </div>
                  
                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-2">
                      Update Interval (Cron Expression)
                    </label>
                    <input
                      type="text"
                      value={configData.updateInterval}
                      onChange={(e) => setConfigData({ ...configData, updateInterval: e.target.value })}
                      placeholder="0 * * * *"
                      className="w-full px-4 py-2 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-blue-500"
                    />
                    <p className="text-xs text-gray-500 mt-1">
                      Cron expression for scheduled updates (e.g., "0 * * * *" = hourly, "0 0 * * *" = daily)
                    </p>
                    <p className="text-xs text-gray-400 mt-1">
                      Note: Changing this requires updating wrangler.toml and redeploying the worker
                    </p>
                  </div>

                  {/* Twitter Auto Post Section */}
                  <div className="border-t border-gray-200 pt-6">
                    <div className="flex items-center justify-between mb-4">
                      <div>
                        <h4 className="text-lg font-semibold text-gray-900">🐦 Twitter Auto Post</h4>
                        <p className="text-sm text-gray-500 mt-1">
                          Automatically post tweets 1-3 times per day at random times
                        </p>
                      </div>
                      <label className="relative inline-flex items-center cursor-pointer">
                        <input
                          type="checkbox"
                          checked={configData.twitterAutoPostEnabled || false}
                          onChange={(e) => setConfigData({ ...configData, twitterAutoPostEnabled: e.target.checked })}
                          className="sr-only peer"
                        />
                        <div className="w-11 h-6 bg-gray-200 peer-focus:outline-none peer-focus:ring-4 peer-focus:ring-blue-300 rounded-full peer peer-checked:after:translate-x-full peer-checked:after:border-white after:content-[''] after:absolute after:top-[2px] after:left-[2px] after:bg-white after:border-gray-300 after:border after:rounded-full after:h-5 after:w-5 after:transition-all peer-checked:bg-blue-600"></div>
                      </label>
                    </div>

                    {/* Status Display */}
                    <div className="bg-gray-50 rounded-lg p-4 space-y-2">
                      <div className="flex items-center justify-between">
                        <span className="text-sm font-medium text-gray-700">Status:</span>
                        <span className={`text-sm font-semibold ${configData.twitterAutoPostEnabled ? 'text-green-600' : 'text-gray-500'}`}>
                          {configData.twitterAutoPostEnabled ? '✓ Enabled' : '✗ Disabled'}
                        </span>
                      </div>
                      <div className="flex items-center justify-between">
                        <span className="text-sm font-medium text-gray-700">Today's Posts:</span>
                        <span className="text-sm text-gray-600">
                          {twitterAutoPostStatus.todaySentCount || 0} / {twitterAutoPostStatus.todayLimit || 3}
                        </span>
                      </div>
                      {twitterAutoPostStatus.date && (
                        <div className="flex items-center justify-between">
                          <span className="text-sm font-medium text-gray-700">Date:</span>
                          <span className="text-sm text-gray-600">{twitterAutoPostStatus.date}</span>
                        </div>
                      )}
                      {configData.twitterAutoPostEnabled && (
                        <div className="mt-3 pt-3 border-t border-gray-200">
                          <p className="text-xs text-gray-500">
                            • Posts are sent at random times throughout the day<br/>
                            • Maximum 3 posts per day<br/>
                            • Mix of protocol analysis and industry insights<br/>
                            • Requires Twitter API and OpenRouter API configuration
                          </p>
                        </div>
                      )}
                    </div>
                  </div>

                  {/* Score Auto Update Section */}
                  <div className="border-t border-gray-200 pt-6">
                    <div className="flex items-center justify-between mb-4">
                      <div>
                        <h4 className="text-lg font-semibold text-gray-900">📊 Score Auto Update</h4>
                        <p className="text-sm text-gray-500 mt-1">
                          Automatically update protocol scores based on TVL tiers
                        </p>
                      </div>
                      <label className="relative inline-flex items-center cursor-pointer">
                        <input
                          type="checkbox"
                          checked={configData.scoreAutoUpdateEnabled || false}
                          onChange={(e) => setConfigData({ ...configData, scoreAutoUpdateEnabled: e.target.checked })}
                          className="sr-only peer"
                        />
                        <div className="w-11 h-6 bg-gray-200 peer-focus:outline-none peer-focus:ring-4 peer-focus:ring-blue-300 rounded-full peer peer-checked:after:translate-x-full peer-checked:after:border-white after:content-[''] after:absolute after:top-[2px] after:left-[2px] after:bg-white after:border-gray-300 after:border after:rounded-full after:h-5 after:w-5 after:transition-all peer-checked:bg-blue-600"></div>
                      </label>
                    </div>

                    {configData.scoreAutoUpdateEnabled && (
                      <div className="bg-gray-50 rounded-lg p-4 space-y-4">
                        <div className="space-y-3">
                          <div>
                            <label className="block text-sm font-medium text-gray-700 mb-1">
                              High TVL (≥$100M) Update Interval
                            </label>
                            <div className="flex items-center gap-2">
                              <input
                                type="number"
                                min="1"
                                value={configData.scoreUpdateIntervalHigh || 168}
                                onChange={(e) => setConfigData({ ...configData, scoreUpdateIntervalHigh: parseInt(e.target.value) || 168 })}
                                className="w-24 px-3 py-2 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-blue-500"
                              />
                              <span className="text-sm text-gray-600">hours</span>
                              <span className="text-xs text-gray-500">
                                ({Math.round((configData.scoreUpdateIntervalHigh || 168) / 24)} days)
                              </span>
                            </div>
                            <p className="text-xs text-gray-500 mt-1">
                              Protocols with TVL ≥ $100M will be updated every {Math.round((configData.scoreUpdateIntervalHigh || 168) / 24)} days
                            </p>
                          </div>

                          <div>
                            <label className="block text-sm font-medium text-gray-700 mb-1">
                              Medium TVL ($10M-$100M) Update Interval
                            </label>
                            <div className="flex items-center gap-2">
                              <input
                                type="number"
                                min="1"
                                value={configData.scoreUpdateIntervalMedium || 336}
                                onChange={(e) => setConfigData({ ...configData, scoreUpdateIntervalMedium: parseInt(e.target.value) || 336 })}
                                className="w-24 px-3 py-2 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-blue-500"
                              />
                              <span className="text-sm text-gray-600">hours</span>
                              <span className="text-xs text-gray-500">
                                ({Math.round((configData.scoreUpdateIntervalMedium || 336) / 24)} days)
                              </span>
                            </div>
                            <p className="text-xs text-gray-500 mt-1">
                              Protocols with TVL $10M-$100M will be updated every {Math.round((configData.scoreUpdateIntervalMedium || 336) / 24)} days
                            </p>
                          </div>

                          <div>
                            <label className="block text-sm font-medium text-gray-700 mb-1">
                              Low TVL (&lt;$10M) Update Interval
                            </label>
                            <div className="flex items-center gap-2">
                              <input
                                type="number"
                                min="1"
                                value={configData.scoreUpdateIntervalLow || 720}
                                onChange={(e) => setConfigData({ ...configData, scoreUpdateIntervalLow: parseInt(e.target.value) || 720 })}
                                className="w-24 px-3 py-2 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-blue-500"
                              />
                              <span className="text-sm text-gray-600">hours</span>
                              <span className="text-xs text-gray-500">
                                ({Math.round((configData.scoreUpdateIntervalLow || 720) / 24)} days)
                              </span>
                            </div>
                            <p className="text-xs text-gray-500 mt-1">
                              Protocols with TVL &lt; $10M will be updated every {Math.round((configData.scoreUpdateIntervalLow || 720) / 24)} days
                            </p>
                          </div>
                        </div>

                        <div className="mt-3 pt-3 border-t border-gray-200">
                          <p className="text-xs text-gray-500">
                            • Updates run automatically during scheduled tasks<br/>
                            • Only protocols that exceed the update interval will be updated<br/>
                            • Each update calls AI API (Gemini 3 Pro) - costs apply<br/>
                            • Recommended: High TVL (7 days), Medium (14 days), Low (30 days)
                          </p>
                        </div>
                      </div>
                    )}

                    {!configData.scoreAutoUpdateEnabled && (
                      <div className="bg-gray-50 rounded-lg p-4">
                        <p className="text-sm text-gray-600">
                          Score auto update is disabled. Enable it to automatically update protocol scores based on TVL tiers.
                        </p>
                      </div>
                    )}
                  </div>
                </div>
              </div>

              <div className="flex items-center justify-end gap-3 p-6 border-t border-gray-200 flex-shrink-0">
                <button
                  type="button"
                  onClick={closeConfigModal}
                  className="px-4 py-2 text-gray-700 bg-gray-100 rounded-lg hover:bg-gray-200 transition-colors"
                >
                  Cancel
                </button>
                <button
                  type="button"
                  onClick={handleSaveConfig}
                  disabled={configLoading}
                  className="px-4 py-2 bg-blue-600 text-white rounded-lg hover:bg-blue-700 transition-colors disabled:opacity-50 disabled:cursor-not-allowed"
                >
                  {configLoading ? 'Saving...' : 'Save Settings'}
                </button>
              </div>
            </div>
          </div>
        )}

        {/* Twitter Modal */}
        {isTwitterModalOpen && (
          <div 
            className="fixed inset-0 z-50 flex items-center justify-center bg-black/40 px-4"
            onClick={closeTwitterModal}
          >
            <div 
              className="w-full max-w-2xl rounded-lg bg-white shadow-2xl max-h-[90vh] flex flex-col"
              onClick={(e) => e.stopPropagation()}
            >
              <div className="flex items-start justify-between p-6 border-b border-gray-200 flex-shrink-0">
                <div>
                  <h3 className="text-2xl font-semibold text-gray-900">DefiSea Twitter Agent</h3>
                  <p className="text-sm text-gray-500 mt-1">Professional content for DefiSea official Twitter account</p>
                </div>
                <button
                  type="button"
                  onClick={closeTwitterModal}
                  className="text-gray-400 hover:text-gray-600 text-2xl"
                  aria-label="Close"
                >
                  ✕
                </button>
              </div>

              <div className="p-6 overflow-y-auto flex-1">
                <div className="space-y-4">
                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-2">
                      Tweet Type
                    </label>
                    <select
                      value={twitterTweetType}
                        onChange={(e) => {
                        setTwitterTweetType(e.target.value);
                        if (e.target.value === 'news_alert' || e.target.value === 'daily_brief' || e.target.value === 'daily_headlines') {
                          loadNewsList();
                        }
                      }}
                      className="w-full px-4 py-2 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-blue-500"
                    >
                      <option value="protocol_analysis">Protocol Analysis</option>
                      <option value="platform_update">Platform Update</option>
                      <option value="industry_insight">Industry Insight</option>
                      <option value="daily_brief">Daily Brief (Multi News)</option>
                      <option value="daily_headlines">Today's DeFi Headlines (Long Tweet)</option>
                      <option value="news_alert">News Alert</option>
                      <option value="custom">Custom Tweet</option>
                    </select>
                    <p className="text-xs text-gray-500 mt-1">
                      Select the type of tweet you want to generate
                    </p>
                  </div>

                  {/* Protocol Analysis */}
                  {twitterTweetType === 'protocol_analysis' && (
                    <div>
                      <label className="block text-sm font-medium text-gray-700 mb-2">
                        Protocol ID
                      </label>
                      <input
                        type="text"
                        value={twitterProtocolId}
                        onChange={(e) => setTwitterProtocolId(e.target.value)}
                        placeholder="Enter protocol ID (e.g., 1, 2, 3)"
                        className="w-full px-4 py-2 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-blue-500"
                      />
                      <p className="text-xs text-gray-500 mt-1">
                        Enter the ID of the protocol to analyze. The AI will generate a professional risk assessment tweet.
                      </p>
                    </div>
                  )}

                  {/* Platform Update */}
                  {twitterTweetType === 'platform_update' && (
                    <div>
                      <label className="block text-sm font-medium text-gray-700 mb-2">
                        Update Content
                      </label>
                      <textarea
                        value={twitterUpdate}
                        onChange={(e) => setTwitterUpdate(e.target.value)}
                        placeholder="Describe the platform update or new feature..."
                        rows={4}
                        className="w-full px-4 py-2 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-blue-500"
                      />
                      <p className="text-xs text-gray-500 mt-1">
                        Describe the update or feature. The AI will generate a professional announcement tweet.
                      </p>
                    </div>
                  )}

                  {/* Industry Insight */}
                  {twitterTweetType === 'industry_insight' && (
                    <div>
                      <label className="block text-sm font-medium text-gray-700 mb-2">
                        Insight Topic
                      </label>
                      <textarea
                        value={twitterInsight}
                        onChange={(e) => setTwitterInsight(e.target.value)}
                        placeholder="Describe the industry insight or trend you want to share..."
                        rows={4}
                        className="w-full px-4 py-2 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-blue-500"
                      />
                      <p className="text-xs text-gray-500 mt-1">
                        Describe the insight or trend. The AI will generate a professional industry analysis tweet.
                      </p>
                    </div>
                  )}

                  {/* Daily Headlines */}
                  {twitterTweetType === 'daily_headlines' && (
                    <div>
                      <div className="p-3 bg-blue-50 border border-blue-200 rounded-lg">
                        <p className="text-sm text-blue-800">
                          📰 <strong>Today's DeFi Headlines</strong>
                        </p>
                        <p className="text-xs text-blue-600 mt-1">
                          This will generate a long-form tweet (up to 25,000 characters) with 10 news headlines from today's DeFi and crypto news. News will be automatically fetched from RSS feeds.
                        </p>
                      </div>
                    </div>
                  )}

                  {/* Custom Tweet */}
                  {twitterTweetType === 'custom' && (
                    <div>
                      <label className="block text-sm font-medium text-gray-700 mb-2">
                        Custom Content
                      </label>
                      <textarea
                        value={twitterCustom}
                        onChange={(e) => setTwitterCustom(e.target.value)}
                        placeholder="Describe what you want to tweet about..."
                        rows={4}
                        className="w-full px-4 py-2 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-blue-500"
                      />
                      <p className="text-xs text-gray-500 mt-1">
                        Describe the topic. The AI will generate a professional tweet in DefiSea's style.
                      </p>
                    </div>
                  )}

                  <div>
                    <button
                      type="button"
                      onClick={handleGenerateTweet}
                      disabled={twitterLoading || (
                        (twitterTweetType === 'protocol_analysis' && !twitterProtocolId) ||
                        (twitterTweetType === 'platform_update' && !twitterUpdate.trim()) ||
                        (twitterTweetType === 'industry_insight' && !twitterInsight.trim()) ||
                        (twitterTweetType === 'news_alert' && newsList.length === 0) ||
                        (twitterTweetType === 'custom' && !twitterCustom.trim())
                        // daily_brief 和 daily_headlines 不需要额外验证，后端会自动获取新闻
                      )}
                      className="w-full px-4 py-2 bg-blue-600 text-white rounded-lg hover:bg-blue-700 transition-colors disabled:opacity-50 disabled:cursor-not-allowed"
                    >
                      {twitterLoading ? 'Generating...' : '🤖 Generate Tweet'}
                    </button>
                  </div>

                  {isThread && generatedThread.length > 0 && (
                    <div>
                      <label className="block text-sm font-medium text-gray-700 mb-2">
                        Generated Thread ({generatedThread.length} tweets)
                      </label>
                      <div className="space-y-3">
                        {generatedThread.map((tweet, idx) => (
                          <div key={idx}>
                            <div className="flex items-center gap-2 mb-1">
                              <span className="text-xs font-medium text-gray-500">Tweet {idx + 1}</span>
                              <span className="text-xs text-gray-400">({tweet.length}/280)</span>
                            </div>
                            <textarea
                              value={tweet}
                              onChange={(e) => {
                                const newThread = [...generatedThread];
                                newThread[idx] = e.target.value;
                                setGeneratedThread(newThread);
                              }}
                              rows={3}
                              maxLength={280}
                              className="w-full px-4 py-2 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-blue-500 text-sm"
                            />
                          </div>
                        ))}
                      </div>
                      <p className="text-xs text-gray-500 mt-2">
                        You can edit each tweet before posting the thread
                      </p>
                    </div>
                  )}

                  {!isThread && generatedTweet && (
                    <div>
                      <label className="block text-sm font-medium text-gray-700 mb-2">
                        Generated Tweet ({generatedTweet.length}/{twitterTweetType === 'daily_headlines' ? '25,000' : '280'})
                      </label>
                      <textarea
                        value={generatedTweet}
                        onChange={(e) => setGeneratedTweet(e.target.value)}
                        rows={twitterTweetType === 'daily_headlines' ? 20 : 4}
                        maxLength={twitterTweetType === 'daily_headlines' ? 25000 : 280}
                        className="w-full px-4 py-2 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-blue-500 font-mono text-sm"
                      />
                      <p className="text-xs text-gray-500 mt-1">
                        {twitterTweetType === 'daily_headlines' 
                          ? 'Long-form tweet (up to 25,000 characters). You can edit before posting.'
                          : 'You can edit the tweet before posting'}
                      </p>
                    </div>
                  )}

                  {twitterError && (
                    <div className="p-3 bg-red-50 border border-red-200 rounded-lg">
                      <p className="text-sm text-red-600">{twitterError}</p>
                    </div>
                  )}

                  {(generatedTweet || (isThread && generatedThread.length > 0)) && (
                    <div>
                      <button
                        type="button"
                        onClick={handlePostTweet}
                        disabled={
                          twitterPosting ||
                          (isThread
                            ? generatedThread.length === 0
                            : !generatedTweet.trim())
                        }
                        className="w-full px-4 py-2 bg-green-600 text-white rounded-lg hover:bg-green-700 transition-colors disabled:opacity-50 disabled:cursor-not-allowed"
                      >
                        {twitterPosting
                          ? 'Posting...'
                          : isThread
                            ? `📤 Post Thread (${generatedThread.length} tweets)`
                            : '📤 Post Tweet'}
                      </button>
                    </div>
                  )}
                </div>
              </div>

              <div className="flex items-center justify-end gap-3 p-6 border-t border-gray-200 flex-shrink-0">
                <button
                  type="button"
                  onClick={closeTwitterModal}
                  className="px-4 py-2 text-gray-700 bg-gray-100 rounded-lg hover:bg-gray-200 transition-colors"
                >
                  Close
                </button>
              </div>
            </div>
          </div>
        )}

        {/* News Modal - 显示协议新闻 */}
        {isNewsModalOpen && (
          <div 
            className="fixed inset-0 z-50 flex items-center justify-center bg-black/40 px-4"
            onClick={() => setIsNewsModalOpen(false)}
          >
            <div 
              className="w-full max-w-4xl rounded-lg bg-white p-6 shadow-2xl max-h-[90vh] overflow-y-auto"
              onClick={(e) => e.stopPropagation()}
            >
              <div className="flex items-start justify-between mb-4">
                <div>
                  <p className="text-sm uppercase tracking-wide text-gray-400">Protocol News</p>
                  <h3 className="text-2xl font-semibold text-gray-900">
                    {newsProtocol?.name || 'Untitled protocol'}
                  </h3>
                  <p className="text-sm text-gray-500">
                    {newsProtocol?.category} • {newsProtocol?.blockchain}
                  </p>
                </div>
                <button
                  type="button"
                  onClick={() => setIsNewsModalOpen(false)}
                  className="text-gray-400 hover:text-gray-600 text-2xl"
                  aria-label="Close news"
                >
                  ✕
                </button>
              </div>

              <div className="mt-6">
                {newsLoading && (
                  <div className="text-center text-gray-500 py-8">Loading news...</div>
                )}

                {newsError && (
                  <div className="rounded-md bg-red-50 p-4 text-sm text-red-600 mb-4">
                    {newsError}
                  </div>
                )}

                {!newsLoading && !newsError && protocolNews.length === 0 && (
                  <div className="text-center text-gray-500 py-8">
                    No news found for this protocol.
                  </div>
                )}

                {!newsLoading && !newsError && protocolNews.length > 0 && (
                  <div className="space-y-4">
                    {protocolNews.map((news, index) => (
                      <div
                        key={news.id || index}
                        className="border border-gray-200 rounded-lg p-4 hover:bg-gray-50 transition-colors"
                      >
                        <div className="flex items-start justify-between">
                          <div className="flex-1">
                            <h4 className="text-lg font-semibold text-gray-900 mb-2">
                              <a
                                href={news.url}
                                target="_blank"
                                rel="noopener noreferrer"
                                className="hover:text-blue-600 transition-colors"
                              >
                                {news.title}
                              </a>
                            </h4>
                            {news.description && (
                              <p className="text-sm text-gray-600 mb-2 line-clamp-2">
                                {news.description}
                              </p>
                            )}
                            <div className="flex items-center gap-4 text-xs text-gray-500">
                              <span className="font-medium">{news.source}</span>
                              {news.published_at && (
                                <span>{formatTimeAgo(news.published_at)}</span>
                              )}
                              {news.category && (
                                <span className="px-2 py-1 bg-gray-100 rounded">
                                  {news.category}
                                </span>
                              )}
                            </div>
                          </div>
                          <a
                            href={news.url}
                            target="_blank"
                            rel="noopener noreferrer"
                            className="ml-4 text-blue-600 hover:text-blue-800 text-sm font-medium"
                          >
                            查看 →
                          </a>
                        </div>
                      </div>
                    ))}
                  </div>
                )}
              </div>

              <div className="mt-6 flex justify-end">
                <button
                  type="button"
                  onClick={() => setIsNewsModalOpen(false)}
                  className="px-4 py-2 text-gray-700 bg-gray-100 rounded-lg hover:bg-gray-200 transition-colors"
                >
                  Close
                </button>
              </div>
            </div>
          </div>
        )}

        <ApyTokenModal
          isOpen={isApyTokenModalOpen}
          onClose={() => setIsApyTokenModalOpen(false)}
          password={password}
        />
      </div>
    </div>
  );
}

export default Admin;

